import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Pencil, Goal, MoveRight, MoveUpRight, Waves,
  Type as TypeIcon, Square, Trash2, Save, X, Circle as CircleIcon,
  Triangle, MousePointer2, Spline, Copy, Undo2, Redo2, Eraser,
  Minus, Grid3x3, AlignVerticalJustifyCenter, Upload, ZoomIn, ZoomOut,
  Maximize, PersonStanding, LifeBuoy, Fence,
  Lock, Unlock, ChevronsUp, ChevronsDown, Minimize, Expand,
  ChevronLeft, ChevronRight, MoveLeft, FileText,
  Group, Ungroup, FlipHorizontal, FlipVertical, BringToFront, SendToBack, Magnet, Ruler,
  Hexagon,
  AlignCenterHorizontal, AlignCenterVertical,
  AlignHorizontalDistributeCenter, AlignVerticalDistributeCenter,
  Columns3, Rows3, Grid2x2, Play, StopCircle, Film, Share2, RotateCw,
} from "lucide-react";
import { toast } from "sonner";

const PITCH_W = 720;
const PITCH_H = 460;
// Formatos reglamentarios (m) para escala y adaptación proporcional
const FORMATS = {
  F11: { realW: 105, realH: 68, label: "F11" },
  F7: { realW: 60, realH: 40, label: "F7" },
  F5: { realW: 40, realH: 20, label: "F5" },
};
const TURF_DARK = "#1F8A47";
const TURF_LIGHT = "#27A056";
const LINE = "#FFFFFF";

const PITCH_TYPES = {
  full:     { label: "Entero",     viewBox: [0, 0, PITCH_W, PITCH_H], center: [PITCH_W / 2, PITCH_H / 2], realW: 105, realH: 68 },
  half:     { label: "Medio",      viewBox: [0, 0, PITCH_W / 2, PITCH_H], center: [PITCH_W / 4, PITCH_H / 2], realW: 52.5, realH: 68 },
  half_box: { label: "Mitad+Área", viewBox: [0, 0, 270, PITCH_H], center: [135, PITCH_H / 2], realW: 40, realH: 68 },
};

const PLAYER_COLORS = {
  morado:   { fill: "#6B21A8", text: "#fff", stroke: "#fff", label: "Morado" },
  blanco:   { fill: "#F8FAFC", text: "#111", stroke: "#64748B", label: "Blanco" },
  rojo:     { fill: "#DC2626", text: "#fff", stroke: "#fff", label: "Rojo" },
  azul:     { fill: "#2563EB", text: "#fff", stroke: "#fff", label: "Azul" },
  amarillo: { fill: "#FACC15", text: "#111", stroke: "#fff", label: "Amarillo" },
  verde:    { fill: "#10B981", text: "#fff", stroke: "#fff", label: "Verde" },
  neutral:  { fill: "#64748B", text: "#fff", stroke: "#fff", label: "Neutral" },
  portero:  { fill: "#F97316", text: "#fff", stroke: "#fff", label: "Portero" },
};
const LEGACY_PLAYER = { home: "azul", away: "rojo", team3: "amarillo", team4: "verde" };
const playerColorKey = (el) =>
  el.type === "player" ? (el.color || "morado") : LEGACY_PLAYER[el.type];
const isPlayerEl = (el) => el.type === "player" || !!LEGACY_PLAYER[el.type];

const ARROW_COLORS = ["#FFFFFF", "#FACC15", "#DC2626", "#2563EB", "#A855F7", "#111827", "#F97316", "#10B981"];
const ZONE_COLORS = {
  amarillo: { fill: "#FACC15", stroke: "#EAB308" },
  morado:   { fill: "#A855F7", stroke: "#7E22CE" },
  rojo:     { fill: "#EF4444", stroke: "#B91C1C" },
  azul:     { fill: "#3B82F6", stroke: "#1D4ED8" },
  verde:    { fill: "#22C55E", stroke: "#15803D" },
  gris:     { fill: "#94A3B8", stroke: "#475569" },
};

const newId = () => Math.random().toString(36).slice(2, 9);

const FORMATIONS = {
  "1-2-3-1": [[0.05, 0.5], [0.18, 0.3], [0.18, 0.7], [0.32, 0.25], [0.32, 0.5], [0.32, 0.75], [0.42, 0.5]],
  "1-3-2-1": [[0.05, 0.5], [0.18, 0.22], [0.18, 0.5], [0.18, 0.78], [0.32, 0.35], [0.32, 0.65], [0.42, 0.5]],
  "1-3-3-1 (F7)": [[0.05, 0.5], [0.18, 0.22], [0.18, 0.5], [0.18, 0.78], [0.32, 0.25], [0.32, 0.5], [0.32, 0.75], [0.42, 0.5]],
  "4-4-2": [[0.05, 0.5], [0.18, 0.15], [0.18, 0.4], [0.18, 0.6], [0.18, 0.85], [0.32, 0.15], [0.32, 0.4], [0.32, 0.6], [0.32, 0.85], [0.45, 0.35], [0.45, 0.65]],
  "4-3-3": [[0.05, 0.5], [0.18, 0.15], [0.18, 0.4], [0.18, 0.6], [0.18, 0.85], [0.32, 0.3], [0.32, 0.5], [0.32, 0.7], [0.45, 0.2], [0.45, 0.5], [0.45, 0.8]],
  "4-2-3-1": [[0.05, 0.5], [0.18, 0.15], [0.18, 0.4], [0.18, 0.6], [0.18, 0.85], [0.28, 0.38], [0.28, 0.62], [0.4, 0.2], [0.4, 0.5], [0.4, 0.8], [0.48, 0.5]],
};

function rondoEls(att, def, { cx, cy, vbW, vbH }) {
  const r = Math.min(vbW, vbH) * 0.22;
  const els = [];
  for (let i = 0; i < att; i++) {
    const a = (2 * Math.PI * i) / att - Math.PI / 2;
    els.push({ type: "player", color: "morado", x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r });
  }
  const rd = def === 1 ? 0 : r * 0.38;
  for (let i = 0; i < def; i++) {
    const a = def === 1 ? 0 : (2 * Math.PI * i) / def + Math.PI / 6;
    els.push({ type: "player", color: "rojo", x: cx + Math.cos(a) * rd, y: cy + Math.sin(a) * rd });
  }
  for (let i = 0; i < 4; i++) {
    const a = (Math.PI / 2) * i + Math.PI / 4;
    els.push({ type: "cone", x: cx + Math.cos(a) * (r + 30), y: cy + Math.sin(a) * (r + 30) });
  }
  els.push({ type: "ball", x: cx + 14, y: cy - r + 18 });
  return els;
}

function posesionEls(n, jokers, { cx, cy }) {
  const w = n <= 4 ? 230 : 300;
  const h = n <= 4 ? 160 : 210;
  const els = [{ type: "zone", x: cx - w / 2, y: cy - h / 2, w, h, zcolor: "amarillo", label: `Posesión ${n}v${n}+${jokers}` }];
  const offs = n <= 4
    ? [[-0.32, -0.25], [-0.32, 0.25], [-0.1, -0.08], [-0.1, 0.08]]
    : [[-0.35, -0.3], [-0.35, 0], [-0.35, 0.3], [-0.12, -0.18], [-0.12, 0.18], [-0.12, 0]];
  offs.forEach(([fx, fy]) => els.push({ type: "player", color: "azul", x: cx + fx * w, y: cy + fy * h }));
  offs.forEach(([fx, fy]) => els.push({ type: "player", color: "rojo", x: cx - fx * w, y: cy + fy * h }));
  els.push({ type: "player", color: "amarillo", label: "C", x: cx, y: cy - h / 2 - 22 });
  els.push({ type: "player", color: "amarillo", label: "C", x: cx, y: cy + h / 2 + 22 });
  if (jokers >= 3) els.push({ type: "player", color: "amarillo", label: "C", x: cx, y: cy });
  els.push({ type: "ball", x: cx - w * 0.32 + 18, y: cy - h * 0.25 });
  return els;
}

function slalomEls({ cx, cy }) {
  const x0 = cx - 150;
  const els = [];
  for (let i = 0; i < 5; i++) els.push({ type: "cone", x: x0 + 40 + i * 55, y: cy + (i % 2 === 0 ? -26 : 26) });
  els.push({ type: "player", color: "morado", x: x0 - 20, y: cy });
  els.push({ type: "ball", x: x0, y: cy });
  els.push({ type: "arrow_drib", x: x0 + 10, y: cy, x2: x0 + 40 + 4 * 55 + 45, y2: cy });
  return els;
}

function circuitoEls({ cx, cy }) {
  const x0 = cx - 210;
  const els = [];
  els.push({ type: "player", color: "morado", x: x0 - 40, y: cy });
  els.push({ type: "ladder", x: x0, y: cy });
  els.push({ type: "arrow_move", x: x0 + 18, y: cy, x2: x0 + 58, y2: cy });
  for (let i = 0; i < 3; i++) els.push({ type: "pole", x: x0 + 85 + i * 42, y: cy + (i % 2 === 0 ? -22 : 22) });
  els.push({ type: "arrow_move", x: x0 + 175, y: cy, x2: x0 + 215, y2: cy });
  els.push({ type: "valla", x: x0 + 240, y: cy - 26 });
  els.push({ type: "valla", x: x0 + 240, y: cy + 26 });
  for (let i = 0; i < 3; i++) els.push({ type: "cone", x: x0 + 300, y: cy - 44 + i * 44 });
  els.push({ type: "arrow_move", x: x0 + 320, y: cy, x2: x0 + 360, y2: cy });
  els.push({ type: "minigoal", x: x0 + 395, y: cy });
  return els;
}

function finalizacionEls({ cx, cy, vbW }) {
  const gx = 34;
  const els = [];
  els.push({ type: "goal", x: gx, y: cy });
  els.push({ type: "player", color: "portero", label: "P", x: gx + 28, y: cy });
  const sx = Math.min(gx + 210, vbW - 60);
  [-70, 0, 70].forEach((dy) => els.push({ type: "player", color: "morado", x: sx, y: cy + dy }));
  els.push({ type: "ball", x: sx - 22, y: cy });
  els.push({ type: "cone", x: sx + 30, y: cy - 95 });
  els.push({ type: "cone", x: sx + 30, y: cy + 95 });
  els.push({ type: "arrow_pass", x: sx - 30, y: cy, x2: gx + 70, y2: cy - 18 });
  return els;
}

function centrosEls({ cx, cy, vbW }) {
  const gx = 34;
  const els = [];
  els.push({ type: "goal", x: gx, y: cy });
  els.push({ type: "player", color: "portero", label: "P", x: gx + 28, y: cy });
  const wx = Math.min(gx + 190, vbW - 80);
  els.push({ type: "player", color: "morado", x: wx, y: cy - 150 });
  els.push({ type: "ball", x: wx - 18, y: cy - 150 });
  els.push({ type: "player", color: "morado", x: wx, y: cy + 150 });
  els.push({ type: "player", color: "morado", x: gx + 130, y: cy - 30 });
  els.push({ type: "player", color: "morado", x: gx + 150, y: cy + 35 });
  els.push({ type: "player", color: "rojo", x: gx + 110, y: cy });
  els.push({ type: "arrow_curve", x: wx, y: cy - 135, x2: gx + 90, y2: cy - 20, cx: (wx + gx) / 2, cy: cy - 110 });
  els.push({ type: "arrow_move", x: gx + 130, y: cy - 30, x2: gx + 95, y2: cy - 22 });
  return els;
}

const TEMPLATE_BUILDERS = {
  "Rondo 3v1": (c) => rondoEls(3, 1, c),
  "Rondo 4v2": (c) => rondoEls(4, 2, c),
  "Rondo 5v2": (c) => rondoEls(5, 2, c),
  "Rondo 6v3": (c) => rondoEls(6, 3, c),
  "Posesión 4v4+2": (c) => posesionEls(4, 2, c),
  "Posesión 6v6+3": (c) => posesionEls(6, 3, c),
  "Conducción slalom": slalomEls,
  "Circuito físico": circuitoEls,
  "Finalización 3 jugadores": finalizacionEls,
  "Centros y remates": centrosEls,
};
const TEMPLATE_GROUPS = [
  { label: "Rondos", items: ["Rondo 3v1", "Rondo 4v2", "Rondo 5v2", "Rondo 6v3"] },
  { label: "Posesión", items: ["Posesión 4v4+2", "Posesión 6v6+3"] },
  { label: "Circuitos", items: ["Conducción slalom", "Circuito físico"] },
  { label: "Finalización", items: ["Finalización 3 jugadores", "Centros y remates"] },
];


function Pitch() {
  const stripes = [];
  for (let i = 0; i < 10; i++) {
    stripes.push(
      <rect key={i} x={(PITCH_W / 10) * i} y={0} width={PITCH_W / 10} height={PITCH_H}
        fill={i % 2 === 0 ? TURF_DARK : TURF_LIGHT} />,
    );
  }
  return (
    <g pointerEvents="none">
      {stripes}
      <rect x={10} y={10} width={PITCH_W - 20} height={PITCH_H - 20} fill="none" stroke={LINE} strokeWidth="2.5" />
      <line x1={PITCH_W / 2} y1={10} x2={PITCH_W / 2} y2={PITCH_H - 10} stroke={LINE} strokeWidth="2" />
      <circle cx={PITCH_W / 2} cy={PITCH_H / 2} r={60} fill="none" stroke={LINE} strokeWidth="2" />
      <circle cx={PITCH_W / 2} cy={PITCH_H / 2} r={3.5} fill={LINE} />
      <rect x={10} y={PITCH_H / 2 - 90} width={120} height={180} fill="none" stroke={LINE} strokeWidth="2" />
      <rect x={10} y={PITCH_H / 2 - 40} width={45} height={80} fill="none" stroke={LINE} strokeWidth="2" />
      <rect x={PITCH_W - 130} y={PITCH_H / 2 - 90} width={120} height={180} fill="none" stroke={LINE} strokeWidth="2" />
      <rect x={PITCH_W - 55} y={PITCH_H / 2 - 40} width={45} height={80} fill="none" stroke={LINE} strokeWidth="2" />
      <rect x={2} y={PITCH_H / 2 - 24} width={8} height={48} fill={LINE} />
      <rect x={PITCH_W - 10} y={PITCH_H / 2 - 24} width={8} height={48} fill={LINE} />
      <path d="M 10 18 A 8 8 0 0 1 18 10" stroke={LINE} strokeWidth="1.5" fill="none" />
      <path d={`M ${PITCH_W - 18} 10 A 8 8 0 0 1 ${PITCH_W - 10} 18`} stroke={LINE} strokeWidth="1.5" fill="none" />
      <path d={`M 10 ${PITCH_H - 18} A 8 8 0 0 0 18 ${PITCH_H - 10}`} stroke={LINE} strokeWidth="1.5" fill="none" />
      <path d={`M ${PITCH_W - 18} ${PITCH_H - 10} A 8 8 0 0 0 ${PITCH_W - 10} ${PITCH_H - 18}`} stroke={LINE} strokeWidth="1.5" fill="none" />
    </g>
  );
}

function ElementShape({ el, selected, onPointerDown }) {
  const startDrag = (e, handle) => {
    e.stopPropagation();
    onPointerDown?.(e, el.id, handle);
  };
  const stroke = selected ? "#FACC15" : "transparent";
  const s = el.scale ?? 1;
  const r = el.rotation ?? 0;
  const transform = `rotate(${r} ${el.x} ${el.y}) translate(${el.x} ${el.y}) scale(${s}) translate(${-el.x} ${-el.y})`;

  const baseRadius = (type) => {
    if (type === "ball" || type === "cone" || type === "seta" || type === "aro") return 14;
    if (type === "pole" || type === "maniqui" || type === "valla") return 22;
    if (type === "ladder") return 50;
    if (type === "goal" || type === "minigoal") return 26;
    return 18;
  };

  const renderHandles = () => {
    if (!selected) return null;
    const br = baseRadius(el.type) * s;
    const angleRad = (r * Math.PI) / 180;
    const rotateX = el.x + Math.sin(angleRad) * (br + 18);
    const rotateY = el.y - Math.cos(angleRad) * (br + 18);
    const scaleX = el.x + Math.cos(angleRad - Math.PI / 4) * (br + 14);
    const scaleY = el.y + Math.sin(angleRad - Math.PI / 4) * (br + 14);
    return (
      <g pointerEvents="all">
        <line x1={el.x} y1={el.y} x2={rotateX} y2={rotateY} stroke="#FACC15" strokeWidth="1" strokeDasharray="2 2" />
        <circle cx={rotateX} cy={rotateY} r={6} fill="#22C55E" stroke="#fff" strokeWidth="1.5" />
        <circle cx={rotateX} cy={rotateY} r={15} fill="transparent"
          onPointerDown={(e) => startDrag(e, "rotate")} style={{ cursor: "grab" }} />
        <circle cx={scaleX} cy={scaleY} r={6} fill="#F59E0B" stroke="#fff" strokeWidth="1.5" />
        <circle cx={scaleX} cy={scaleY} r={15} fill="transparent"
          onPointerDown={(e) => startDrag(e, "scale")} style={{ cursor: "nwse-resize" }} />
      </g>
    );
  };

  if (isPlayerEl(el)) {
    const c = PLAYER_COLORS[playerColorKey(el)] || PLAYER_COLORS.morado;
    const isGk = playerColorKey(el) === "portero";
    return (
      <g>
        <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
          <circle cx={el.x} cy={el.y} r={16} fill={c.fill} stroke={c.stroke} strokeWidth="2" />
          <text x={el.x} y={el.y + 5} textAnchor="middle" fontSize="14" fontWeight="800" fill={c.text}>
            {el.label ?? (isGk ? "P" : "")}
          </text>
          {el.name && (
            <text x={el.x} y={el.y + 30} textAnchor="middle" fontSize="9" fontWeight="700"
              fill="#fff" stroke="#0F172A" strokeWidth="2.5" paintOrder="stroke">
              {el.name}
            </text>
          )}
          {selected && <circle cx={el.x} cy={el.y} r={20} fill="none" stroke={stroke} strokeWidth="2" strokeDasharray="3 2" />}
        </g>
        {renderHandles()}
      </g>
    );
  }

  switch (el.type) {
    case "ball":
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <circle cx={el.x} cy={el.y} r={12} fill="#FFFFFF" stroke="#111" strokeWidth="1.5" />
            <polygon points={`${el.x},${el.y - 5} ${el.x + 4.8},${el.y - 1.5} ${el.x + 3},${el.y + 4} ${el.x - 3},${el.y + 4} ${el.x - 4.8},${el.y - 1.5}`} fill="#111" />
            <line x1={el.x} y1={el.y - 5} x2={el.x} y2={el.y - 12} stroke="#111" strokeWidth="1" />
            <line x1={el.x + 4.8} y1={el.y - 1.5} x2={el.x + 11.5} y2={el.y - 3.5} stroke="#111" strokeWidth="1" />
            <line x1={el.x + 3} y1={el.y + 4} x2={el.x + 7} y2={el.y + 9.5} stroke="#111" strokeWidth="1" />
            <line x1={el.x - 3} y1={el.y + 4} x2={el.x - 7} y2={el.y + 9.5} stroke="#111" strokeWidth="1" />
            <line x1={el.x - 4.8} y1={el.y - 1.5} x2={el.x - 11.5} y2={el.y - 3.5} stroke="#111" strokeWidth="1" />
            {selected && <circle cx={el.x} cy={el.y} r={15} fill="none" stroke={stroke} strokeWidth="2" strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    case "cone":
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <polygon points={`${el.x},${el.y - 12} ${el.x - 9},${el.y + 8} ${el.x + 9},${el.y + 8}`}
              fill="#F97316" stroke="#fff" strokeWidth="1.5" />
            {selected && <rect x={el.x - 13} y={el.y - 16} width={26} height={28} fill="none" stroke={stroke} strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    case "seta":
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <ellipse cx={el.x} cy={el.y + 3} rx={9} ry={3.5} fill="#EAB308" stroke="#fff" strokeWidth="1" />
            <path d={`M ${el.x - 8} ${el.y + 2} A 8 8 0 0 1 ${el.x + 8} ${el.y + 2}`} fill="#FDE047" stroke="#CA8A04" strokeWidth="1" />
            {selected && <rect x={el.x - 12} y={el.y - 10} width={24} height={20} fill="none" stroke={stroke} strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    case "aro":
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <circle cx={el.x} cy={el.y} r={11} fill="none" stroke="#F59E0B" strokeWidth="3.5" />
            {selected && <circle cx={el.x} cy={el.y} r={15} fill="none" stroke={stroke} strokeWidth="2" strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    case "valla":
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <rect x={el.x - 14} y={el.y - 10} width={3} height={20} fill="#DC2626" />
            <rect x={el.x + 11} y={el.y - 10} width={3} height={20} fill="#DC2626" />
            <rect x={el.x - 14} y={el.y - 10} width={28} height={4} fill="#FCA5A5" stroke="#DC2626" strokeWidth="1" />
            {selected && <rect x={el.x - 18} y={el.y - 14} width={36} height={28} fill="none" stroke={stroke} strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    case "maniqui":
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <circle cx={el.x} cy={el.y - 12} r={5} fill="#FBBF24" stroke="#92400E" strokeWidth="1" />
            <path d={`M ${el.x - 7} ${el.y + 14} L ${el.x - 4} ${el.y - 7} L ${el.x + 4} ${el.y - 7} L ${el.x + 7} ${el.y + 14} Z`}
              fill="#FBBF24" stroke="#92400E" strokeWidth="1" />
            {selected && <rect x={el.x - 11} y={el.y - 20} width={22} height={38} fill="none" stroke={stroke} strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    case "goal":
    case "minigoal": {
      const gw = el.type === "goal" ? 20 : 11;
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <rect x={el.x - gw} y={el.y - 6} width={gw * 2} height={12} fill="#fff" stroke="#111" strokeWidth="1.5" />
            <line x1={el.x - gw} y1={el.y + 6} x2={el.x - gw + 4} y2={el.y + 12} stroke="#111" strokeWidth="1.5" />
            <line x1={el.x + gw} y1={el.y + 6} x2={el.x + gw - 4} y2={el.y + 12} stroke="#111" strokeWidth="1.5" />
            {selected && <rect x={el.x - gw - 4} y={el.y - 10} width={gw * 2 + 8} height={26} fill="none" stroke={stroke} strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    }
    case "zone": {
      const zc = ZONE_COLORS[el.zcolor] || ZONE_COLORS.amarillo;
      const zw = el.w || 100, zh = el.h || 70;
      const isCircle = el.shape === "circle";
      const isPoly = el.shape === "poly" && Array.isArray(el.points);
      return (
        <g>
          {isPoly ? (
            <polygon points={el.points.map((p) => p.join(",")).join(" ")}
              fill={zc.fill} fillOpacity={el.opacity ?? 0.25} stroke={zc.stroke} strokeWidth="2" strokeDasharray="6 4"
              onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }} />
          ) : isCircle ? (
            <ellipse cx={el.x + zw / 2} cy={el.y + zh / 2} rx={zw / 2} ry={zh / 2}
              fill={zc.fill} fillOpacity={el.opacity ?? 0.25} stroke={zc.stroke} strokeWidth="2" strokeDasharray="6 4"
              onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }} />
          ) : (
            <rect x={el.x} y={el.y} width={zw} height={zh}
              fill={zc.fill} fillOpacity={el.opacity ?? 0.25} stroke={zc.stroke} strokeWidth="2" strokeDasharray="6 4"
              onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }} />
          )}
          {el.label && (
            <text x={el.x + zw / 2} y={el.y + 15} textAnchor="middle" fontSize="11" fontWeight="800"
              fill="#fff" stroke="#0F172A" strokeWidth="2.5" paintOrder="stroke" pointerEvents="none">
              {el.label}
            </text>
          )}
          {selected && !isPoly && (
            <>
              <rect x={(el.x + zw) - 7} y={(el.y + zh) - 7} width={14} height={14}
                fill={zc.fill} stroke="#334155" strokeWidth="1" />
              <rect x={(el.x + zw) - 15} y={(el.y + zh) - 15} width={30} height={30}
                fill="transparent"
                onPointerDown={(e) => startDrag(e, "resize")} style={{ cursor: "nwse-resize" }} />
            </>
          )}
        </g>
      );
    }
    case "measure": {
      return (
        <g>
          <line x1={el.x} y1={el.y} x2={el.x2} y2={el.y2} stroke="#0EA5E9" strokeWidth="2.5" strokeDasharray="5 3"
            onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }} />
          <circle cx={el.x} cy={el.y} r="3.5" fill="#0EA5E9" stroke="#fff" strokeWidth="1" />
          <circle cx={el.x2} cy={el.y2} r="3.5" fill="#0EA5E9" stroke="#fff" strokeWidth="1" />
          {selected && (
            <>
              <circle cx={el.x} cy={el.y} r="9" fill="transparent"
                onPointerDown={(e) => startDrag(e, "start")} style={{ cursor: "grab" }} />
              <circle cx={el.x2} cy={el.y2} r="9" fill="transparent"
                onPointerDown={(e) => startDrag(e, "end")} style={{ cursor: "grab" }} />
            </>
          )}
        </g>
      );
    }
    case "text":
      return (
        <g onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
          <rect x={el.x - 4} y={el.y - 14} width={Math.max(40, (el.label || "Texto").length * 7.5)} height={20}
            fill="#fff" fillOpacity="0.85" stroke={selected ? "#FACC15" : "transparent"} strokeWidth="1.5" rx="3" />
          <text x={el.x} y={el.y} fontSize={el.fontSize || 13} fontWeight="700" fill="#111">
            {el.label || "Texto"}
          </text>
        </g>
      );
    case "pole":
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <rect x={el.x - 1.5} y={el.y - 18} width={3} height={32} fill="#0EA5E9" />
            <rect x={el.x - 6} y={el.y + 12} width={12} height={3} fill="#0EA5E9" />
            <rect x={el.x - 1.5} y={el.y - 12} width={3} height={4} fill="#fff" />
            <rect x={el.x - 1.5} y={el.y - 2} width={3} height={4} fill="#fff" />
            <rect x={el.x - 1.5} y={el.y + 6} width={3} height={4} fill="#fff" />
            {selected && <rect x={el.x - 10} y={el.y - 22} width={20} height={40} fill="none" stroke={stroke} strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    case "ladder": {
      const lw = 22, lh = 90, rungs = 6;
      return (
        <g>
          <g transform={transform} onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }}>
            <rect x={el.x - lw / 2} y={el.y - lh / 2} width={lw} height={lh} fill="#94A3B8" stroke="#475569" strokeWidth="1.5" />
            {Array.from({ length: rungs - 1 }).map((_, i) => {
              const yLine = el.y - lh / 2 + (lh / rungs) * (i + 1);
              return <line key={i} x1={el.x - lw / 2} y1={yLine} x2={el.x + lw / 2} y2={yLine} stroke="#475569" strokeWidth="1.5" />;
            })}
            {selected && <rect x={el.x - lw / 2 - 4} y={el.y - lh / 2 - 4} width={lw + 8} height={lh + 8} fill="none" stroke={stroke} strokeDasharray="3 2" />}
          </g>
          {renderHandles()}
        </g>
      );
    }
    case "line": {
      const pathD = `M ${el.x} ${el.y} L ${el.x2} ${el.y2}`;
      return (
        <g>
          <path d={pathD} stroke="transparent" strokeWidth="14" fill="none"
            onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }} />
          <path d={pathD} stroke={el.stroke || "#FFFFFF"} strokeWidth={el.width || 3} fill="none"
            strokeDasharray={el.dashed ? "6 5" : undefined} pointerEvents="none" />
          {selected && (
            <>
              <circle cx={el.x} cy={el.y} r={6} fill="#FACC15" stroke="#854D0E" />
              <circle cx={el.x} cy={el.y} r={15} fill="transparent"
                onPointerDown={(e) => startDrag(e, "start")} style={{ cursor: "grab" }} />
              <circle cx={el.x2} cy={el.y2} r={6} fill="#FACC15" stroke="#854D0E" />
              <circle cx={el.x2} cy={el.y2} r={15} fill="transparent"
                onPointerDown={(e) => startDrag(e, "end")} style={{ cursor: "grab" }} />
            </>
          )}
        </g>
      );
    }
    case "arrow_move":
    case "arrow_pass":
    case "arrow_drib":
    case "arrow_rotation":
    case "arrow_curve": {
      const defColor =
        el.type === "arrow_pass" ? "#FFFFFF"
          : el.type === "arrow_drib" ? "#F59E0B"
          : el.type === "arrow_curve" ? "#A855F7"
          : el.type === "arrow_rotation" ? "#A855F7"
          : "#1D4ED8";
      const color = el.stroke || defColor;
      const strokeW = el.width || 3;
      const dash = el.dashed === true ? "6 5"
        : el.dashed === false ? undefined
        : (el.type === "arrow_pass" ? "6 5" : undefined);
      const x1 = el.x, y1 = el.y, x2 = el.x2, y2 = el.y2;
      let pathD;
      if (el.type === "arrow_drib") {
        const dx = x2 - x1, dy = y2 - y1;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len, uy = dy / len;
        const nx = -uy, ny = ux;
        const segs = Math.max(4, Math.floor(len / 14));
        let d = `M ${x1} ${y1}`;
        for (let i = 1; i <= segs; i++) {
          const t = i / segs;
          const px = x1 + dx * t + nx * 5 * Math.sin(i * Math.PI);
          const py = y1 + dy * t + ny * 5 * Math.sin(i * Math.PI);
          d += ` L ${px} ${py}`;
        }
        pathD = d;
      } else if (el.type === "arrow_curve") {
        const cX = el.cx ?? (x1 + x2) / 2;
        const cY = el.cy ?? (y1 + y2) / 2 - 40;
        pathD = `M ${x1} ${y1} Q ${cX} ${cY} ${x2} ${y2}`;
      } else if (el.type === "arrow_rotation") {
        const rr = Math.max(12, Math.hypot(x2 - x1, y2 - y1) / 2);
        pathD = `M ${x1} ${y1} A ${rr} ${rr} 0 1 1 ${x2} ${y2}`;
      } else {
        pathD = `M ${x1} ${y1} L ${x2} ${y2}`;
      }
      const arrowId = `ah-${el.id}`;
      const ctrlX = el.cx ?? (x1 + x2) / 2;
      const ctrlY = el.cy ?? (y1 + y2) / 2 - 40;
      return (
        <g>
          <defs>
            <marker id={arrowId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
            </marker>
          </defs>
          <path d={pathD} stroke="transparent" strokeWidth="14" fill="none"
            onPointerDown={(e) => startDrag(e, "body")} style={{ cursor: "grab" }} />
          <path d={pathD} stroke={color} strokeWidth={strokeW} fill="none"
            strokeDasharray={dash} markerEnd={`url(#${arrowId})`} pointerEvents="none" />
          {selected && (
            <>
              <circle cx={x1} cy={y1} r={6} fill="#FACC15" stroke="#854D0E" />
              <circle cx={x1} cy={y1} r={15} fill="transparent"
                onPointerDown={(e) => startDrag(e, "start")} style={{ cursor: "grab" }} />
              <circle cx={x2} cy={y2} r={6} fill="#FACC15" stroke="#854D0E" />
              <circle cx={x2} cy={y2} r={15} fill="transparent"
                onPointerDown={(e) => startDrag(e, "end")} style={{ cursor: "grab" }} />
              {el.type === "arrow_curve" && (
                <>
                  <line x1={(x1 + x2) / 2} y1={(y1 + y2) / 2} x2={ctrlX} y2={ctrlY}
                    stroke="#A855F7" strokeWidth="1" strokeDasharray="2 3" pointerEvents="none" />
                  <circle cx={ctrlX} cy={ctrlY} r={6} fill="#A855F7" stroke="#fff" />
                  <circle cx={ctrlX} cy={ctrlY} r={15} fill="transparent"
                    onPointerDown={(e) => startDrag(e, "control")} style={{ cursor: "grab" }} />
                </>
              )}
            </>
          )}
        </g>
      );
    }
    default:
      return null;
  }
}

function FieldOverlays({ vb, thirds, lanes, grid }) {
  const [x0, y0, w, h] = vb;
  const els = [];
  if (grid) {
    const step = 40;
    for (let gx = x0 + step; gx < x0 + w; gx += step) {
      els.push(<line key={`gx${gx}`} x1={gx} y1={y0} x2={gx} y2={y0 + h} stroke="#fff" strokeOpacity="0.18" strokeWidth="1" />);
    }
    for (let gy = y0 + step; gy < y0 + h; gy += step) {
      els.push(<line key={`gy${gy}`} x1={x0} y1={gy} x2={x0 + w} y2={gy} stroke="#fff" strokeOpacity="0.18" strokeWidth="1" />);
    }
  }
  if (thirds) {
    [1, 2].forEach((i) => els.push(
      <line key={`t${i}`} x1={x0 + (w / 3) * i} y1={y0} x2={x0 + (w / 3) * i} y2={y0 + h}
        stroke="#FDE047" strokeOpacity="0.75" strokeWidth="2" strokeDasharray="10 6" />,
    ));
  }
  if (lanes) {
    [0.18, 0.365, 0.635, 0.82].forEach((f, i) => els.push(
      <line key={`l${i}`} x1={x0} y1={y0 + h * f} x2={x0 + w} y2={y0 + h * f}
        stroke="#93C5FD" strokeOpacity="0.75" strokeWidth="2" strokeDasharray="10 6" />,
    ));
  }
  return <g pointerEvents="none">{els}</g>;
}

const MATERIAL_PRESETS = [
  { type: "ball", label: "Balón", Icon: CircleIcon },
  { type: "cone", label: "Cono", Icon: Triangle },
  { type: "seta", label: "Seta", Icon: CircleIcon },
  { type: "pole", label: "Pica", Icon: AlignVerticalJustifyCenter },
  { type: "aro", label: "Aro", Icon: LifeBuoy },
  { type: "valla", label: "Valla", Icon: Fence },
  { type: "maniqui", label: "Maniquí", Icon: PersonStanding },
  { type: "ladder", label: "Escalera", Icon: Grid3x3 },
  { type: "goal", label: "Portería", Icon: Goal },
  { type: "minigoal", label: "Miniport.", Icon: Goal },
];
const LINE_PRESETS = [
  { type: "arrow_move", label: "Mov.", Icon: MoveRight },
  { type: "arrow_pass", label: "Pase", Icon: MoveUpRight },
  { type: "arrow_curve", label: "Curva", Icon: Spline },
  { type: "arrow_drib", label: "Cond.", Icon: Waves },
  { type: "line", label: "Línea", Icon: Minus },
  { type: "zone", label: "Zona", Icon: Square },
  { type: "text", label: "Texto", Icon: TypeIcon },
];
// Flechas con significado táctico normalizado (RFEF/formación) + leyenda automática.
const TACTIC_PRESETS = [
  { key: "pase", label: "Pase", desc: "Pase — línea continua", base: "arrow_pass", stroke: "#FFFFFF", width: 3, dashed: false, Icon: MoveUpRight },
  { key: "desplazamiento", label: "Desplaz.", desc: "Desplazamiento — línea discontinua", base: "arrow_move", stroke: "#FFFFFF", width: 3, dashed: true, Icon: MoveRight },
  { key: "conduccion", label: "Conducción", desc: "Conducción — línea ondulada", base: "arrow_drib", stroke: "#F59E0B", width: 3, dashed: false, Icon: Waves },
  { key: "tiro", label: "Tiro", desc: "Tiro — flecha gruesa", base: "arrow_pass", stroke: "#111827", width: 6, dashed: false, Icon: MoveUpRight },
  { key: "presion", label: "Presión", desc: "Presión — flecha roja", base: "arrow_move", stroke: "#DC2626", width: 4, dashed: false, Icon: MoveRight },
  { key: "repliegue", label: "Repliegue", desc: "Repliegue — flecha discontinua hacia atrás", base: "arrow_move", stroke: "#DC2626", width: 3, dashed: true, Icon: MoveRight },
  { key: "desmarque", label: "Desmarque", desc: "Desmarque — línea curva", base: "arrow_curve", stroke: "#FFFFFF", width: 3, dashed: false, Icon: Spline },
  { key: "centro", label: "Centro", desc: "Centro — línea curva al área", base: "arrow_curve", stroke: "#38BDF8", width: 3, dashed: false, Icon: Spline },
  { key: "rotacion", label: "Rotación", desc: "Rotación — flecha circular", base: "arrow_rotation", stroke: "#A855F7", width: 3, dashed: false, Icon: RotateCw },
];
const TACTIC_BY_KEY = Object.fromEntries(TACTIC_PRESETS.map((t) => [t.key, t]));
const DUP_OPTIONS = [2, 3, 4, 5, 6, 8, 10, 11];

export default function TacticalBoard({
  open, onOpenChange,
  trainingId, taskKey, teamId,
  defaultTitle = "", defaultDescription = "", defaultObjective = "",
  onSaved,
  imageOnly = false, scopeId = null,
  initialBoardData = null, viewOnly = false,
}) {
  const [elements, setElements] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [pitchType, setPitchType] = useState("full");
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const [overlays, setOverlays] = useState({ thirds: false, lanes: false, grid: false });
  const [scenes, setScenes] = useState([{ id: "s0", els: [] }]);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [animSpeed, setAnimSpeed] = useState(1200);
  const [pendingAutoplay, setPendingAutoplay] = useState(false);
  const [exportingGif, setExportingGif] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [snapGrid, setSnapGrid] = useState(false);
  const [showGuides, setShowGuides] = useState(false);
  const [showMeters, setShowMeters] = useState(false);
  const [meterStep, setMeterStep] = useState(10);
  const [tool, setTool] = useState(null); // "poly" | "measure"
  const [polyPts, setPolyPts] = useState([]);
  const polyRef = useRef([]);
  const measureRef = useRef(null);
  const [fmt, setFmt] = useState("F11");
  const GRID_STEP = 25;
  const animRef = useRef(null);
  const [marquee, setMarquee] = useState(null);
  const [groupColor, setGroupColor] = useState("morado");
  const [groupCount, setGroupCount] = useState(4);
  const [title, setTitle] = useState(defaultTitle);
  const [description, setDescription] = useState(defaultDescription);
  const [objective, setObjective] = useState(defaultObjective);
  const [tagsText, setTagsText] = useState("");
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [shareWithClub, setShareWithClub] = useState(false);
  const [saving, setSaving] = useState(false);
  const [counts, setCounts] = useState({});
  const [roster, setRoster] = useState([]);
  const [rosterSel, setRosterSel] = useState("");
  const svgRef = useRef(null);
  const clipboardRef = useRef(null);

  // Fase B — modo completo tablet/móvil
  const [fsActive, setFsActive] = useState(false);
  const [toolbarCollapsed, setToolbarCollapsed] = useState(false);
  const [lockField, setLockField] = useState(false);
  const [interacting, setInteracting] = useState(false);
  const dialogContentRef = useRef(null);
  const draftReadyRef = useRef(false);
  const draftKey = trainingId && taskKey
    ? `board_draft::${trainingId}::${taskKey}`
    : (scopeId ? `board_draft::scope::${scopeId}` : null);
  const showTools = !toolbarCollapsed && !interacting;
  const clearDraft = () => { try { if (draftKey) localStorage.removeItem(draftKey); } catch { /* noop */ } };

  const selSet = new Set(selectedIds);
  const singleSel = selectedIds.length === 1
    ? elements.find((e) => e.id === selectedIds[0]) || null
    : null;

  const historyRef = useRef({ stack: [[]], index: 0 });
  const [, setHistoryTick] = useState(0);
  const MAX_HISTORY = 60;

  const snapshot = (els) => {
    const h = historyRef.current;
    const nextJson = JSON.stringify(els);
    if (JSON.stringify(h.stack[h.index]) === nextJson) return;
    h.stack = h.stack.slice(0, h.index + 1);
    h.stack.push(JSON.parse(nextJson));
    if (h.stack.length > MAX_HISTORY) h.stack.shift();
    h.index = h.stack.length - 1;
    setHistoryTick((t) => t + 1);
  };

  const undo = () => {
    const h = historyRef.current;
    if (h.index <= 0) return;
    h.index -= 1;
    setElements(JSON.parse(JSON.stringify(h.stack[h.index])));
    setSelectedIds([]);
    setHistoryTick((t) => t + 1);
  };

  const redo = () => {
    const h = historyRef.current;
    if (h.index >= h.stack.length - 1) return;
    h.index += 1;
    setElements(JSON.parse(JSON.stringify(h.stack[h.index])));
    setSelectedIds([]);
    setHistoryTick((t) => t + 1);
  };

  const canUndo = historyRef.current.index > 0;
  const canRedo = historyRef.current.index < historyRef.current.stack.length - 1;

  const commit = (updater) => {
    setElements((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      queueMicrotask(() => snapshot(next));
      return next;
    });
  };

  const clearBoard = () => {
    if (elements.length === 0) return;
    if (!window.confirm("¿Vaciar la pizarra? Se eliminarán todos los elementos.")) return;
    commit([]);
    setSelectedIds([]);
    setCounts({});
  };

  const nextLabel = (colorKey, extra = 1) => {
    const n = (counts[colorKey] || 0) + extra;
    setCounts((c) => ({ ...c, [colorKey]: n }));
    return n;
  };

  const applyFormation = (key, colorKey = "morado") => {
    const positions = FORMATIONS[key];
    if (!positions) return;
    const [, , vbW, vbH] = PITCH_TYPES[pitchType].viewBox;
    const startN = counts[colorKey] || 0;
    const newOnes = positions.map((p, i) => ({
      id: newId(), type: "player", color: colorKey,
      x: p[0] * vbW, y: p[1] * vbH, label: String(startN + i + 1),
    }));
    setCounts((c) => ({ ...c, [colorKey]: startN + positions.length }));
    commit((xs) => [...xs, ...newOnes]);
    setSelectedIds([]);
  };

  const applyTemplate = (key) => {
    const builder = TEMPLATE_BUILDERS[key];
    if (!builder) return;
    const [, , vbW, vbH] = PITCH_TYPES[pitchType].viewBox;
    const [cx, cy] = PITCH_TYPES[pitchType].center;
    const raw = builder({ cx, cy, vbW, vbH });
    const countsPatch = {};
    const withIds = raw.map((el) => {
      const out = { ...el, id: newId(), x: clamp(el.x, 24, vbW - 24), y: clamp(el.y, 24, vbH - 24) };
      if (el.x2 != null) {
        out.x2 = clamp(el.x2, 24, vbW - 24);
        out.y2 = clamp(el.y2, 24, vbH - 24);
      }
      if (el.type === "player" && el.label == null) {
        const ck = el.color || "morado";
        countsPatch[ck] = (countsPatch[ck] ?? counts[ck] ?? 0) + 1;
        out.label = String(countsPatch[ck]);
      }
      return out;
    });
    if (Object.keys(countsPatch).length) setCounts((c) => ({ ...c, ...countsPatch }));
    commit((xs) => [...xs, ...withIds]);
    setSelectedIds(withIds.map((e) => e.id));
    toast.success(`Ejercicio "${key}" añadido`);
  };

  const addGroup = (colorKey, n) => {
    const [cx, cy] = PITCH_TYPES[pitchType].center;
    const [, , vbW, vbH] = PITCH_TYPES[pitchType].viewBox;
    const radius = Math.min(vbW, vbH) * 0.26;
    const startN = counts[colorKey] || 0;
    const newOnes = Array.from({ length: n }).map((_, i) => {
      const a = (2 * Math.PI * i) / n - Math.PI / 2;
      return {
        id: newId(), type: "player", color: colorKey,
        x: clamp(cx + Math.cos(a) * radius, 20, PITCH_W - 20),
        y: clamp(cy + Math.sin(a) * radius, 20, PITCH_H - 20),
        label: String(startN + i + 1),
      };
    });
    setCounts((c) => ({ ...c, [colorKey]: startN + n }));
    commit((xs) => [...xs, ...newOnes]);
    setSelectedIds(newOnes.map((e) => e.id));
  };

  useEffect(() => {
    if (open) {
      draftReadyRef.current = false;
      setTool(null);
      polyRef.current = [];
      setPolyPts([]);
      measureRef.current = null;
      setElements([]);
      setSelectedIds([]);
      setPitchType("full");
      setZoom(1);
      setPan({ x: 0, y: 0 });
      pointersRef.current.clear();
      pinchRef.current = null;
      setOverlays({ thirds: false, lanes: false, grid: false });
      if (animRef.current) cancelAnimationFrame(animRef.current);
      setScenes([{ id: newId(), els: [] }]);
      setSceneIdx(0);
      setPlaying(false);
      setMarquee(null);
      setCounts({});
      setTitle(defaultTitle);
      setDescription(defaultDescription);
      setObjective(defaultObjective);
      setTagsText("");
      setShareWithClub(false);
      historyRef.current = { stack: [[]], index: 0 };
      setHistoryTick((t) => t + 1);
      if (initialBoardData?.scenes?.length) {
        const sc = JSON.parse(JSON.stringify(initialBoardData.scenes));
        setScenes(sc);
        setSceneIdx(0);
        setElements(JSON.parse(JSON.stringify(sc[0].els || [])));
        if (initialBoardData.pitchType && PITCH_TYPES[initialBoardData.pitchType]) {
          setPitchType(initialBoardData.pitchType);
        }
        historyRef.current = { stack: [JSON.parse(JSON.stringify(sc[0].els || []))], index: 0 };
        const cnts = {};
        sc.forEach((s) => (s.els || []).forEach((el) => {
          if (el.type === "player") {
            const n = parseInt(el.label, 10);
            if (!Number.isNaN(n)) {
              const k = el.color || "morado";
              cnts[k] = Math.max(cnts[k] || 0, n);
            }
          }
        }));
        setCounts(cnts);
        setPendingAutoplay(viewOnly && sc.length > 1);
      }
      // Recuperar edición sin guardar (autoguardado local)
      if (!viewOnly && draftKey) {
        try {
          const raw = localStorage.getItem(draftKey);
          if (raw) {
            const d = JSON.parse(raw);
            const hasContent = d?.scenes?.some((s) => (s.els || []).length);
            if (hasContent && window.confirm("Se encontró una edición sin guardar de esta pizarra. ¿Quieres recuperarla?")) {
              const sc = JSON.parse(JSON.stringify(d.scenes));
              setScenes(sc);
              setSceneIdx(0);
              setElements(JSON.parse(JSON.stringify(sc[0].els || [])));
              if (d.pitchType && PITCH_TYPES[d.pitchType]) setPitchType(d.pitchType);
              historyRef.current = { stack: [JSON.parse(JSON.stringify(sc[0].els || []))], index: 0 };
            } else if (!hasContent) {
              localStorage.removeItem(draftKey);
            } else {
              localStorage.removeItem(draftKey);
            }
          }
        } catch { /* noop */ }
      }
      draftReadyRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pantalla completa real (inmersiva) en tablet/móvil
  useEffect(() => {
    const onFs = () => setFsActive(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  useEffect(() => { if (!open && document.fullscreenElement) document.exitFullscreen?.().catch(() => {}); }, [open]);

  const toggleFullscreen = () => {
    const el = dialogContentRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  };

  // Autoguardado local del borrador (debounce)
  useEffect(() => {
    if (!open || viewOnly || !draftKey || playing || !draftReadyRef.current) return undefined;
    const id = setTimeout(() => {
      try {
        const all = scenesWithCurrent().map((s) => ({ id: s.id, els: s.els }));
        if (all.some((s) => (s.els || []).length)) {
          localStorage.setItem(draftKey, JSON.stringify({ pitchType, scenes: all, ts: Date.now() }));
        }
      } catch { /* noop */ }
    }, 800);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements, scenes, sceneIdx, pitchType, open, playing, viewOnly, draftKey]);

  // Plantilla del equipo: solo asistentes del entrenamiento; si no hay asistencia, todos
  useEffect(() => {
    if (!open || !teamId || viewOnly) {
      setRoster([]);
      return;
    }
    (async () => {
      try {
        const pl = await api.get(`/players?team_id=${teamId}`);
        let list = pl.data || [];
        if (trainingId) {
          const at = await api.get(`/trainings/${trainingId}/attendance`).catch(() => null);
          const recs = at?.data?.items || [];
          if (recs.length) {
            const attending = new Set(recs.filter((a) => a.status === "asiste").map((a) => a.player_id));
            if (attending.size) list = list.filter((p) => attending.has(p.id));
          }
        }
        list.sort((a, b) => (a.dorsal ?? 999) - (b.dorsal ?? 999));
        setRoster(list);
      } catch {
        setRoster([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, teamId, trainingId]);

  const usedPlayerIds = useMemo(() => {
    const ids = new Set();
    elements.forEach((el) => el.playerId && ids.add(el.playerId));
    scenes.forEach((s, i) => {
      if (i !== sceneIdx) (s.els || []).forEach((el) => el.playerId && ids.add(el.playerId));
    });
    return ids;
  }, [elements, scenes, sceneIdx]);
  const availableRoster = roster.filter((p) => !usedPlayerIds.has(p.id));

  const addRosterPlayer = (pid) => {
    const p = roster.find((x) => x.id === pid);
    if (!p) return;
    const [cx, cy] = PITCH_TYPES[pitchType].center;
    const id = newId();
    const n = usedPlayerIds.size;
    const el = {
      id, type: "player", color: "morado",
      x: cx + (n % 5) * 26 - 52,
      y: cy + Math.floor(n / 5) * 30,
      label: p.dorsal != null && p.dorsal !== "" ? String(p.dorsal) : (p.name || "?").trim()[0].toUpperCase(),
      name: (p.name || "").trim().split(/\s+/).slice(0, 2).join(" ").slice(0, 16),
      playerId: p.id,
    };
    commit((xs) => [...xs, el]);
    setSelectedIds([id]);
    setRosterSel("");
  };

  const addPlayer = (colorKey) => {
    const [cx, cy] = PITCH_TYPES[pitchType].center;
    const id = newId();
    const n = nextLabel(colorKey);
    const el = {
      id, type: "player", color: colorKey,
      x: cx + ((n - 1) % 5) * 24 - 48, y: cy + Math.floor((n - 1) / 5) * 24,
      label: colorKey === "portero" ? "P" : String(n),
    };
    commit((xs) => [...xs, el]);
    setSelectedIds([id]);
  };

  const addElement = (type, opts = {}) => {
    const [cx, cy] = PITCH_TYPES[pitchType].center;
    const id = newId();
    let el;
    if (type === "text") {
      const txt = window.prompt("Texto a mostrar:", "Etiqueta");
      if (!txt) return;
      el = { id, type, x: cx, y: cy - 60, label: txt };
    } else if (type === "zone") {
      el = { id, type, x: cx - 50, y: cy - 35, w: 100, h: 70 };
    } else if (type.startsWith("arrow_") || type === "line") {
      el = { id, type, x: cx - 60, y: cy, x2: cx + 60, y2: cy };
      if (type === "arrow_curve") {
        el.cx = cx;
        el.cy = cy - 40;
      }
    } else if (type === "goal") {
      el = { id, type, x: 30, y: cy };
    } else {
      el = { id, type, x: cx, y: cy + (type === "cone" || type === "seta" ? 40 : 0) };
    }
    commit((xs) => [...xs, { ...el, ...opts }]);
    setSelectedIds([id]);
  };

  const addTactic = (key) => {
    const preset = TACTIC_BY_KEY[key];
    if (!preset) return;
    const [cx, cy] = PITCH_TYPES[pitchType].center;
    const id = newId();
    const el = {
      id, type: preset.base, x: cx - 60, y: cy, x2: cx + 60, y2: cy,
      stroke: preset.stroke, width: preset.width, dashed: preset.dashed, tactic: key,
    };
    if (preset.base === "arrow_curve") { el.cx = cx; el.cy = cy - 40; }
    commit((xs) => [...xs, el]);
    setSelectedIds([id]);
  };

  const removeSelected = () => {
    if (!selectedIds.length) return;
    const ids = new Set(selectedIds);
    commit((xs) => xs.filter((e) => !ids.has(e.id)));
    setSelectedIds([]);
  };

  const makeCopy = (original, offX, offY, labelOverride) => {
    const copy = {
      ...original,
      id: newId(),
      x: clamp((original.x || 0) + offX, 0, PITCH_W),
      y: clamp((original.y || 0) + offY, 0, PITCH_H),
    };
    if (labelOverride != null) copy.label = labelOverride;
    if (original.x2 != null) {
      copy.x2 = clamp(original.x2 + offX, 0, PITCH_W);
      copy.y2 = clamp(original.y2 + offY, 0, PITCH_H);
    }
    if (original.cx != null) {
      copy.cx = clamp(original.cx + offX, 0, PITCH_W);
      copy.cy = clamp(original.cy + offY, 0, PITCH_H);
    }
    return copy;
  };

  const duplicateSelected = (times = 1) => {
    if (!selectedIds.length) return;
    const originals = elements.filter((e) => selSet.has(e.id));
    const copies = [];
    const countsPatch = {};
    originals.forEach((orig) => {
      const ck = isPlayerEl(orig) ? playerColorKey(orig) : null;
      for (let i = 1; i <= times; i++) {
        let label;
        if (ck && ck !== "portero") {
          countsPatch[ck] = (countsPatch[ck] ?? counts[ck] ?? 0) + 1;
          label = String(countsPatch[ck]);
        }
        copies.push(makeCopy(orig, 26 * i, 20 * ((i % 3) === 0 ? 1 : 0) + 6 * (i % 2), label));
      }
    });
    if (Object.keys(countsPatch).length) setCounts((c) => ({ ...c, ...countsPatch }));
    commit((xs) => [...xs, ...copies]);
    setSelectedIds(copies.map((c) => c.id));
  };

  const patchSelected = (patch) => {
    if (!selectedIds.length) return;
    const ids = new Set(selectedIds);
    commit((xs) => xs.map((e) => (ids.has(e.id) ? { ...e, ...patch } : e)));
  };

  const moveElBy = (el, dx, dy) => {
    const upd = { ...el, x: el.x + dx, y: el.y + dy };
    if (el.x2 != null) { upd.x2 = el.x2 + dx; upd.y2 = el.y2 + dy; }
    if (el.cx != null) { upd.cx = el.cx + dx; upd.cy = el.cy + dy; }
    return upd;
  };

  const deepCopy = (v) => JSON.parse(JSON.stringify(v));

  const scenesWithCurrent = () =>
    scenes.map((s, i) => (i === sceneIdx ? { ...s, els: deepCopy(elements) } : s));

  const loadSceneEls = (els) => {
    setElements(deepCopy(els));
    setSelectedIds([]);
    historyRef.current = { stack: [deepCopy(els)], index: 0 };
    setHistoryTick((t) => t + 1);
  };

  const switchScene = (idx) => {
    if (playing || idx === sceneIdx) return;
    const n = scenesWithCurrent();
    setScenes(n);
    setSceneIdx(idx);
    loadSceneEls(n[idx].els);
  };

  const addScene = () => {
    if (playing) return;
    if (scenes.length >= 8) {
      toast.error("Máximo 8 escenas por jugada");
      return;
    }
    const n = scenesWithCurrent();
    const copy = { id: newId(), els: deepCopy(elements) };
    setScenes([...n.slice(0, sceneIdx + 1), copy, ...n.slice(sceneIdx + 1)]);
    setSceneIdx(sceneIdx + 1);
    toast.success(`Escena ${sceneIdx + 2} creada (copia de la actual). Mueve los elementos para la siguiente fase.`);
  };

  const deleteScene = () => {
    if (playing || scenes.length <= 1) return;
    const n = scenes.filter((_, i) => i !== sceneIdx);
    const newIdx = Math.max(0, sceneIdx - 1);
    setScenes(n);
    setSceneIdx(newIdx);
    loadSceneEls(n[newIdx].els);
  };

  const easeT = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

  useEffect(() => {
    if (!pendingAutoplay || playing || scenes.length < 2) return undefined;
    const t = setTimeout(() => {
      setPendingAutoplay(false);
      playAnimation();
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoplay, scenes.length]);

  const stopAnimation = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = null;
    setPlaying(false);
    loadSceneEls(scenes[sceneIdx].els);
  };

  const lerpScenes = (fromEls, toEls, k) => {
    const fromMap = Object.fromEntries(fromEls.map((el) => [el.id, el]));
    return toEls.map((el) => {
      const from = fromMap[el.id];
      if (!from) return el;
      const L = (a, b) => a + (b - a) * k;
      const out = { ...el, x: L(from.x, el.x), y: L(from.y, el.y) };
      if (el.x2 != null && from.x2 != null) { out.x2 = L(from.x2, el.x2); out.y2 = L(from.y2, el.y2); }
      if (el.cx != null && from.cx != null) { out.cx = L(from.cx, el.cx); out.cy = L(from.cy, el.cy); }
      if (el.rotation != null || from.rotation != null) out.rotation = L(from.rotation ?? 0, el.rotation ?? 0);
      if (el.scale != null || from.scale != null) out.scale = L(from.scale ?? 1, el.scale ?? 1);
      return out;
    });
  };

  const exportGif = async () => {
    const all = scenesWithCurrent();
    if (all.length < 2) {
      toast.info("Crea al menos 2 escenas para compartir la jugada");
      return;
    }
    setScenes(all);
    setSelectedIds([]);
    setExportingGif(true);
    try {
      const { GIFEncoder, quantize, applyPalette } = await import("gifenc");
      const W = 640;
      const H = Math.round((W * baseVB[3]) / baseVB[2]);
      const fps = 12;
      const delay = Math.round(1000 / fps);
      const framesPerSeg = Math.max(6, Math.round((animSpeed / 1000) * fps));
      const gif = GIFEncoder();
      const snapshot = async () => {
        const svg = svgRef.current;
        const clone = svg.cloneNode(true);
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clone.setAttribute("viewBox", baseVB.join(" "));
        const xml = new XMLSerializer().serializeToString(clone);
        const url = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
        try {
          const img = await new Promise((res, rej) => {
            const i = new Image();
            i.onload = () => res(i);
            i.onerror = rej;
            i.src = url;
          });
          const canvas = document.createElement("canvas");
          canvas.width = W;
          canvas.height = H;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, W, H);
          return ctx.getImageData(0, 0, W, H);
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      const addFrame = (imgData, d = delay) => {
        const palette = quantize(imgData.data, 256);
        const index = applyPalette(imgData.data, palette);
        gif.writeFrame(index, W, H, { palette, delay: d });
      };
      flushSync(() => setElements(deepCopy(all[0].els)));
      addFrame(await snapshot(), 900);
      for (let seg = 0; seg < all.length - 1; seg++) {
        for (let s = 1; s <= framesPerSeg; s++) {
          const k = easeT(s / framesPerSeg);
          flushSync(() => setElements(lerpScenes(all[seg].els, all[seg + 1].els, k)));
          addFrame(await snapshot());
        }
        addFrame(await snapshot(), seg === all.length - 2 ? 1400 : 600);
      }
      gif.finish();
      const blob = new Blob([gif.bytes()], { type: "image/gif" });
      const file = new File([blob], "jugada.gif", { type: "image/gif" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Jugada táctica" });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "jugada.gif";
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
        toast.success("GIF descargado: adjúntalo en WhatsApp para compartir la jugada");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("No se pudo generar el GIF");
    } finally {
      setExportingGif(false);
      setElements(deepCopy(all[Math.min(sceneIdx, all.length - 1)].els));
    }
  };

  const moveScene = (dir) => {
    if (playing) return;
    const target = sceneIdx + dir;
    if (target < 0 || target >= scenes.length) return;
    const n = scenesWithCurrent();
    const [moved] = n.splice(sceneIdx, 1);
    n.splice(target, 0, moved);
    setScenes(n);
    setSceneIdx(target);
    loadSceneEls(n[target].els);
  };

  const exportPhasesPdf = async () => {
    const all = scenesWithCurrent();
    setScenes(all);
    setSelectedIds([]);
    setExportingPdf(true);
    try {
      const W = 1000;
      const H = Math.round((W * baseVB[3]) / baseVB[2]);
      const snapshotURL = async () => {
        const svg = svgRef.current;
        const clone = svg.cloneNode(true);
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        clone.setAttribute("viewBox", baseVB.join(" "));
        const xml = new XMLSerializer().serializeToString(clone);
        const url = URL.createObjectURL(new Blob([xml], { type: "image/svg+xml;charset=utf-8" }));
        try {
          const img = await new Promise((res, rej) => {
            const i = new Image();
            i.onload = () => res(i);
            i.onerror = rej;
            i.src = url;
          });
          const canvas = document.createElement("canvas");
          canvas.width = W;
          canvas.height = H;
          const ctx = canvas.getContext("2d");
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, W, H);
          ctx.drawImage(img, 0, 0, W, H);
          return canvas.toDataURL("image/png");
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      const phaseImgs = [];
      for (let i = 0; i < all.length; i++) {
        flushSync(() => setElements(deepCopy(all[i].els)));
        // eslint-disable-next-line no-await-in-loop
        phaseImgs.push({ label: `Fase ${i + 1}`, image_png: await snapshotURL() });
      }
      const seen = new Set();
      const legend = [];
      all.forEach((sc) => (sc.els || []).forEach((el) => {
        if (el.tactic && TACTIC_BY_KEY[el.tactic] && !seen.has(el.tactic)) {
          seen.add(el.tactic);
          legend.push({ label: TACTIC_BY_KEY[el.tactic].label, desc: TACTIC_BY_KEY[el.tactic].desc });
        }
      }));
      const resp = await api.post(
        "/export/board/phases-pdf",
        { title: title || "Secuencia táctica por fases", phases: phaseImgs, legend },
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "secuencia_fases.pdf";
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      toast.success("PDF por fases descargado");
    } catch (e) {
      toast.error("No se pudo generar el PDF por fases");
    } finally {
      setExportingPdf(false);
      setElements(deepCopy(all[Math.min(sceneIdx, all.length - 1)].els));
    }
  };

  const playAnimation = () => {
    const all = scenesWithCurrent();
    if (all.length < 2) {
      toast.info("Crea al menos 2 escenas para animar la jugada");
      return;
    }
    setScenes(all);
    setSelectedIds([]);
    setPlaying(true);
    let seg = 0;
    let start = null;
    const step = (ts) => {
      if (start == null) start = ts;
      const t = Math.min(1, (ts - start) / animSpeed);
      setElements(lerpScenes(all[seg].els, all[seg + 1].els, easeT(t)));
      setSceneIdx(seg + 1);
      if (t < 1) {
        animRef.current = requestAnimationFrame(step);
      } else if (seg < all.length - 2) {
        seg += 1;
        start = null;
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
        setPlaying(false);
        setSceneIdx(all.length - 1);
        loadSceneEls(all[all.length - 1].els);
      }
    };
    setElements(deepCopy(all[0].els));
    animRef.current = requestAnimationFrame(step);
  };

  const alignSelected = (mode) => {
    if (selectedIds.length < 2) return;
    const sel = elements.filter((e) => selSet.has(e.id));
    if (mode === "h" || mode === "v") {
      const avg = sel.reduce((s, e) => s + (mode === "h" ? e.y : e.x), 0) / sel.length;
      commit((xs) => xs.map((e) => {
        if (!selSet.has(e.id)) return e;
        return mode === "h" ? moveElBy(e, 0, avg - e.y) : moveElBy(e, avg - e.x, 0);
      }));
    } else {
      const axis = mode === "dist-h" ? "x" : "y";
      const sorted = [...sel].sort((a, b) => a[axis] - b[axis]);
      const min = sorted[0][axis];
      const max = sorted[sorted.length - 1][axis];
      const step = sorted.length > 1 ? (max - min) / (sorted.length - 1) : 0;
      const targets = {};
      sorted.forEach((e, i) => { targets[e.id] = min + step * i; });
      commit((xs) => xs.map((e) => {
        if (!(e.id in targets)) return e;
        const d = targets[e.id] - e[axis];
        return axis === "x" ? moveElBy(e, d, 0) : moveElBy(e, 0, d);
      }));
    }
  };

  const snapV = (v) => (snapGrid ? Math.round(v / GRID_STEP) * GRID_STEP : v);

  const finishPoly = () => {
    const pts = polyRef.current;
    if (pts.length >= 3) {
      const id = newId();
      commit((xs) => [...xs, {
        id, type: "zone", shape: "poly", points: pts,
        x: pts[0][0], y: pts[0][1], zcolor: "amarillo",
      }]);
      setSelectedIds([id]);
    }
    polyRef.current = [];
    setPolyPts([]);
    setTool(null);
  };
  const cancelDraw = () => { polyRef.current = []; setPolyPts([]); measureRef.current = null; setTool(null); };

  const adaptFormat = (next) => {
    if (next === fmt) return;
    const from = FORMATS[fmt];
    const to = FORMATS[next];
    if (elements.length && window.confirm(`¿Reescalar la tarea de ${from.label} a ${to.label}? Las posiciones se adaptan proporcionalmente.`)) {
      const rx = to.realW / from.realW;
      const ry = to.realH / from.realH;
      const [cx, cy] = _pt.center;
      const sc = (v, c, r) => c + (v - c) * r;
      commit((xs) => xs.map((e) => {
        const u = { ...e };
        if (e.x != null) u.x = sc(e.x, cx, rx);
        if (e.y != null) u.y = sc(e.y, cy, ry);
        if (e.x2 != null) u.x2 = sc(e.x2, cx, rx);
        if (e.y2 != null) u.y2 = sc(e.y2, cy, ry);
        if (e.cx != null) u.cx = sc(e.cx, cx, rx);
        if (e.cy != null) u.cy = sc(e.cy, cy, ry);
        if (e.w != null) u.w = e.w * rx;
        if (e.h != null) u.h = e.h * ry;
        if (e.points) u.points = e.points.map(([px, py]) => [sc(px, cx, rx), sc(py, cy, ry)]);
        return u;
      }));
    }
    setFmt(next);
  };

  const groupSelected = () => {
    if (selectedIds.length < 2) return;
    const gid = newId();
    commit((xs) => xs.map((e) => (selSet.has(e.id) ? { ...e, groupId: gid } : e)));
    toast.success("Elementos agrupados");
  };
  const ungroupSelected = () => {
    commit((xs) => xs.map((e) => (selSet.has(e.id) ? { ...e, groupId: undefined } : e)));
  };
  const toggleLockSelected = () => {
    const anyUnlocked = elements.some((e) => selSet.has(e.id) && !e.locked);
    commit((xs) => xs.map((e) => (selSet.has(e.id) ? { ...e, locked: anyUnlocked } : e)));
  };
  const bringToFront = () => {
    commit((xs) => [...xs.filter((e) => !selSet.has(e.id)), ...xs.filter((e) => selSet.has(e.id))]);
  };
  const sendToBack = () => {
    commit((xs) => [...xs.filter((e) => selSet.has(e.id)), ...xs.filter((e) => !selSet.has(e.id))]);
  };
  const flipSelected = (axis) => {
    const sel = elements.filter((e) => selSet.has(e.id));
    if (!sel.length) return;
    const xsArr = [];
    const ysArr = [];
    sel.forEach((e) => {
      xsArr.push(e.x); ysArr.push(e.y);
      if (e.x2 != null) { xsArr.push(e.x2); ysArr.push(e.y2); }
      if (e.cx != null) { xsArr.push(e.cx); ysArr.push(e.cy); }
    });
    const cx = (Math.min(...xsArr) + Math.max(...xsArr)) / 2;
    const cy = (Math.min(...ysArr) + Math.max(...ysArr)) / 2;
    const mir = (v, c) => 2 * c - v;
    commit((xs) => xs.map((e) => {
      if (!selSet.has(e.id)) return e;
      const u = { ...e };
      if (axis === "h") {
        u.x = mir(e.x, cx);
        if (e.x2 != null) u.x2 = mir(e.x2, cx);
        if (e.cx != null) u.cx = mir(e.cx, cx);
        if (e.rotation != null) u.rotation = -e.rotation;
      } else {
        u.y = mir(e.y, cy);
        if (e.y2 != null) u.y2 = mir(e.y2, cy);
        if (e.cy != null) u.cy = mir(e.cy, cy);
        if (e.rotation != null) u.rotation = 180 - e.rotation;
      }
      return u;
    }));
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (playing) return;
      const meta = e.ctrlKey || e.metaKey;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (meta && k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((meta && k === "y") || (meta && e.shiftKey && k === "z")) { e.preventDefault(); redo(); }
      else if (meta && k === "a") {
        e.preventDefault();
        setSelectedIds(elements.map((el) => el.id));
      } else if (meta && k === "d") {
        e.preventDefault();
        duplicateSelected(1);
      } else if (meta && k === "c") {
        if (selectedIds.length) {
          e.preventDefault();
          clipboardRef.current = elements.filter((el) => selSet.has(el.id)).map((el) => ({ ...el }));
        }
      } else if (meta && k === "v") {
        if (clipboardRef.current?.length) {
          e.preventDefault();
          const pasted = clipboardRef.current.map((el) => makeCopy(el, 30, 30));
          commit((xs) => [...xs, ...pasted]);
          setSelectedIds(pasted.map((p) => p.id));
        }
      } else if (e.key === "Escape") {
        setSelectedIds([]);
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (selectedIds.length) {
          e.preventDefault();
          removeSelected();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedIds, elements]);

  const dragState = useRef(null);

  const svgPoint = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const { x, y } = pt.matrixTransform(ctm.inverse());
    return { x, y };
  };

  const onPointerDown = (e, id, handle) => {
    if (playing || lockField) return;
    e.preventDefault();
    if (e.pointerType === "touch") setInteracting(true);
    const clicked = elements.find((el) => el.id === id);
    if (clicked?.locked) { setSelectedIds([id]); return; }
    let ids = selectedIds;
    if (e.shiftKey) {
      ids = selSet.has(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id];
      setSelectedIds(ids);
      if (!ids.includes(id)) return;
    } else if (!selSet.has(id)) {
      ids = clicked?.groupId
        ? elements.filter((el) => el.groupId === clicked.groupId).map((el) => el.id)
        : [id];
      setSelectedIds(ids);
    } else if (clicked?.groupId) {
      ids = elements.filter((el) => el.groupId === clicked.groupId).map((el) => el.id);
      setSelectedIds(ids);
    }
    const { x, y } = svgPoint(e.clientX, e.clientY);
    const moveIds = handle === "body" ? ids : [id];
    const originals = {};
    elements.forEach((el) => {
      if (moveIds.includes(el.id)) originals[el.id] = { ...el };
    });
    dragState.current = { id, handle, startPointer: { x, y }, originals };
  };

  const clampPan = (p, z) => {
    const vb = PITCH_TYPES[pitchType].viewBox;
    const maxX = (vb[2] - vb[2] / z) / 2;
    const maxY = (vb[3] - vb[3] / z) / 2;
    return { x: clamp(p.x, -maxX, maxX), y: clamp(p.y, -maxY, maxY) };
  };

  const onPointerMove = (e) => {
    if (pointersRef.current.has(e.pointerId)) {
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    }
    if (pinchRef.current && pointersRef.current.size >= 2) {
      const pts = [...pointersRef.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const center = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      const p = pinchRef.current;
      const newZoom = clamp(Math.round(p.startZoom * (dist / p.startDist) * 100) / 100, 1, 3);
      setZoom(newZoom);
      const rect = svgRef.current?.getBoundingClientRect();
      if (rect) {
        const vb = PITCH_TYPES[pitchType].viewBox;
        const unitsPerPx = (vb[2] / newZoom) / rect.width;
        setPan(clampPan({
          x: p.startPan.x - (center.x - p.startCenter.x) * unitsPerPx,
          y: p.startPan.y - (center.y - p.startCenter.y) * unitsPerPx,
        }, newZoom));
      }
      return;
    }
    if (marquee) {
      const { x, y } = svgPoint(e.clientX, e.clientY);
      setMarquee((m) => ({ ...m, x1: x, y1: y }));
      return;
    }
    if (!dragState.current) return;
    const { id, handle, startPointer, originals } = dragState.current;
    const { x, y } = svgPoint(e.clientX, e.clientY);
    const dx = x - startPointer.x, dy = y - startPointer.y;
    setElements((xs) =>
      xs.map((el) => {
        const original = originals[el.id];
        if (!original) return el;
        if (el.id === id) {
          if (handle === "start" && original.x2 != null) {
            return { ...el, x: clamp(original.x + dx, 0, PITCH_W), y: clamp(original.y + dy, 0, PITCH_H) };
          }
          if (handle === "end" && original.x2 != null) {
            return { ...el, x2: clamp(original.x2 + dx, 0, PITCH_W), y2: clamp(original.y2 + dy, 0, PITCH_H) };
          }
          if (handle === "control" && original.cx != null) {
            return { ...el, cx: clamp(original.cx + dx, 0, PITCH_W), cy: clamp(original.cy + dy, 0, PITCH_H) };
          }
          if (handle === "resize" && original.w != null) {
            return { ...el, w: Math.max(20, original.w + dx), h: Math.max(20, original.h + dy) };
          }
          if (handle === "rotate") {
            const a0 = Math.atan2(startPointer.y - original.y, startPointer.x - original.x);
            const a1 = Math.atan2(y - original.y, x - original.x);
            const delta = ((a1 - a0) * 180) / Math.PI;
            let newRot = (original.rotation ?? 0) + delta;
            while (newRot > 180) newRot -= 360;
            while (newRot <= -180) newRot += 360;
            return { ...el, rotation: newRot };
          }
          if (handle === "scale") {
            const d0 = Math.hypot(startPointer.x - original.x, startPointer.y - original.y) || 1;
            const d1 = Math.hypot(x - original.x, y - original.y) || 1;
            const newScale = Math.max(0.4, Math.min(3.0, (original.scale ?? 1) * (d1 / d0)));
            return { ...el, scale: newScale };
          }
        }
        const updates = { ...el, x: clamp(snapV(original.x + dx), 0, PITCH_W), y: clamp(snapV(original.y + dy), 0, PITCH_H) };
        if (original.x2 != null) {
          updates.x2 = clamp(snapV(original.x2 + dx), 0, PITCH_W);
          updates.y2 = clamp(snapV(original.y2 + dy), 0, PITCH_H);
        }
        if (original.cx != null) {
          updates.cx = clamp(snapV(original.cx + dx), 0, PITCH_W);
          updates.cy = clamp(snapV(original.cy + dy), 0, PITCH_H);
        }
        if (original.points) {
          updates.points = original.points.map(([px, py]) => [
            clamp(snapV(px + dx), 0, PITCH_W), clamp(snapV(py + dy), 0, PITCH_H)]);
          updates.x = updates.points[0][0];
          updates.y = updates.points[0][1];
        }
        return updates;
      }),
    );
  };

  const elInRect = (el, rx0, ry0, rx1, ry1) => {
    const minX = Math.min(rx0, rx1), maxX = Math.max(rx0, rx1);
    const minY = Math.min(ry0, ry1), maxY = Math.max(ry0, ry1);
    const pts = [[el.x, el.y]];
    if (el.x2 != null) pts.push([el.x2, el.y2]);
    if (el.w != null) pts.push([el.x + el.w / 2, el.y + el.h / 2]);
    return pts.some(([px, py]) => px >= minX && px <= maxX && py >= minY && py <= maxY);
  };

  const onPointerUp = (e) => {
    if (e?.pointerType === "touch") setInteracting(false);
    if (e?.pointerId != null) pointersRef.current.delete(e.pointerId);
    if (pinchRef.current) {
      if (pointersRef.current.size < 2) pinchRef.current = null;
      return;
    }
    if (marquee) {
      const { x0, y0, x1, y1 } = marquee;
      if (Math.abs(x1 - x0) > 6 || Math.abs(y1 - y0) > 6) {
        const hits = elements.filter((el) => elInRect(el, x0, y0, x1, y1)).map((el) => el.id);
        setSelectedIds(e?.shiftKey ? Array.from(new Set([...selectedIds, ...hits])) : hits);
      } else if (!e?.shiftKey) {
        setSelectedIds([]);
      }
      setMarquee(null);
      return;
    }
    if (dragState.current) {
      setElements((els) => {
        queueMicrotask(() => snapshot(els));
        return els;
      });
    }
    dragState.current = null;
  };

  const onSvgPointerDown = (e) => {
    if (playing) return;
    if (tool === "measure" || tool === "poly") {
      if (e.target !== svgRef.current) return;
      const p = svgPoint(e.clientX, e.clientY);
      if (tool === "measure") {
        if (!measureRef.current) {
          measureRef.current = { x: p.x, y: p.y };
        } else {
          const a = measureRef.current;
          const id = newId();
          commit((xs) => [...xs, { id, type: "measure", x: a.x, y: a.y, x2: p.x, y2: p.y }]);
          measureRef.current = null;
          setTool(null);
        }
      } else {
        polyRef.current = [...polyRef.current, [p.x, p.y]];
        setPolyPts(polyRef.current.slice());
      }
      return;
    }
    if (e.pointerType === "touch") setInteracting(true);
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const pts = [...pointersRef.current.values()];
      pinchRef.current = {
        startDist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1,
        startZoom: zoom,
        startPan: { ...pan },
        startCenter: { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 },
      };
      setMarquee(null);
      dragState.current = null;
      return;
    }
    if (e.target === svgRef.current && !lockField) {
      const { x, y } = svgPoint(e.clientX, e.clientY);
      setMarquee({ x0: x, y0: y, x1: x, y1: y });
    }
  };

  const onWheel = (e) => {
    const dir = e.deltaY > 0 ? -0.15 : 0.15;
    setZoom((z) => Math.round(clamp(z + dir, 1, 3) * 100) / 100);
  };

  const baseVB = PITCH_TYPES[pitchType].viewBox;
  const vbW = baseVB[2] / zoom;
  const vbH = baseVB[3] / zoom;
  const cpPan = clampPan(pan, zoom);
  const vbX = baseVB[0] + (baseVB[2] - vbW) / 2 + cpPan.x;
  const vbY = baseVB[1] + (baseVB[3] - vbH) / 2 + cpPan.y;

  // Escala real (metros) — según el formato elegido (F11/F7/F5)
  const _pt = PITCH_TYPES[pitchType];
  const _fmt = FORMATS[fmt] || FORMATS.F11;
  const _inset = 10;
  const _playW = baseVB[2] - _inset * 2;
  const _playH = baseVB[3] - _inset * 2;
  const _realW = _fmt.realW * (pitchType === "full" ? 1 : _pt.realW / FORMATS.F11.realW);
  const _realH = _fmt.realH;
  const uPerMx = _playW / _realW;
  const uPerMy = _playH / _realH;
  const meterLines = { v: [], h: [] };
  if (showMeters) {
    for (let m = 0; m <= _realW + 0.01; m += meterStep) {
      meterLines.v.push({ x: _inset + m * uPerMx, m: Math.round(m) });
    }
    for (let m = 0; m <= _realH + 0.01; m += meterStep) {
      meterLines.h.push({ y: _inset + m * uPerMy, m: Math.round(m) });
    }
  }
  const zoneIds = elements.filter((e) => e.type === "zone").map((e) => e.id);
  const playerCount = elements.filter((e) => e.type === "player").length;
  const fieldArea = Math.round(_realW * _realH);
  const densityPerPlayer = playerCount > 0 ? Math.round(fieldArea / playerCount) : null;

  const exportPng = async () => {
    const svg = svgRef.current;
    if (!svg) throw new Error("SVG no disponible");
    const clone = svg.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("viewBox", baseVB.join(" "));
    const xml = new XMLSerializer().serializeToString(clone);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = (err) => reject(err);
        i.src = url;
      });
      const scale = 2;
      const canvas = document.createElement("canvas");
      canvas.width = baseVB[2] * scale;
      canvas.height = baseVB[3] * scale;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      return await new Promise((resolve, reject) => {
        canvas.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error("toBlob falló"));
        }, "image/png", 0.95);
      });
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const fileInputRef = useRef(null);
  const ALLOWED_IMG_EXT = ["jpg", "jpeg", "png", "webp"];
  const MAX_IMG_BYTES = 10 * 1024 * 1024;

  const persistImageFile = async (file) => {
    const boardData = elements.length
      ? { pitchType, scenes: scenesWithCurrent().map((s) => ({ id: s.id, els: s.els })) }
      : null;
    const fd = new FormData();
    fd.append("scope", imageOnly ? "task_template" : "training_task");
    fd.append("scope_id", imageOnly ? (scopeId || `board_${Date.now()}`) : `${trainingId}::${taskKey}`);
    fd.append("file", file);
    const r = await api.post("/attachments", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const attachmentId = r.data.id;

    if (!imageOnly && saveToLibrary && title.trim()) {
      const parsedTags = tagsText
        .split(",")
        .map((t) => t.trim().toLowerCase().replace(/^#/, ""))
        .filter(Boolean);
      const tags = Array.from(new Set(["pizarra", ...parsedTags]));
      try {
        await api.post("/task-templates", {
          team_id: shareWithClub ? null : (teamId || null),
          title: title.trim(),
          description: description.trim(),
          objective: objective.trim(),
          image_id: attachmentId,
          tags,
          board_data: boardData,
        });
      } catch (err) {
        toast.warning(`Imagen guardada, pero no se pudo añadir a la biblioteca: ${formatApiError(err)}`);
      }
    }

    onSaved?.(attachmentId, {
      description: description.trim(),
      objective: objective.trim(),
      boardData,
    });
    return attachmentId;
  };

  const handleSave = async () => {
    if (elements.length === 0) {
      toast.error("Añade al menos un elemento a la pizarra o usa 'Subir imagen'");
      return;
    }
    setSaving(true);
    try {
      const blob = await exportPng();
      const file = new File([blob], `tarea_${Date.now()}.png`, { type: "image/png" });
      await persistImageFile(file);
      toast.success("Tarea guardada");
      clearDraft();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo guardar la pizarra");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImage = () => {
    fileInputRef.current?.click();
  };

  const onPickImageFile = async (e) => {
    const rawFile = e.target.files?.[0];
    e.target.value = "";
    if (!rawFile) return;
    const ext = (rawFile.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_IMG_EXT.includes(ext)) {
      toast.error("Formato no permitido. Usa JPG, PNG o WebP.");
      return;
    }
    if (rawFile.size > MAX_IMG_BYTES) {
      toast.error("La imagen supera los 10 MB.");
      return;
    }
    setSaving(true);
    try {
      const file = await compressImage(rawFile);
      await persistImageFile(file);
      toast.success("Imagen subida correctamente");
      clearDraft();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(formatApiError(err) || "No se pudo subir la imagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent ref={dialogContentRef} className={`board-dialog w-screen max-w-none h-[100dvh] max-h-[100dvh] rounded-none p-2 sm:w-full sm:max-w-6xl sm:h-auto sm:max-h-[95vh] sm:rounded-lg sm:p-4 overflow-y-auto ${fsActive ? "board-fs bg-white sm:max-w-none sm:h-[100dvh] sm:max-h-[100dvh] sm:rounded-none" : ""}`}>
        <style>{`.board-fs button{min-height:40px}.board-fs [data-testid^="board-tactic-"],.board-fs [data-testid^="board-add-"]{height:44px;font-size:13px;padding:0 12px}.board-fs [data-testid="board-lock-field"],.board-fs [data-testid="board-fullscreen"],.board-fs [data-testid="board-toolbar-collapse"]{height:44px}`}</style>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-purple-600" />
            {viewOnly ? "Animación de la jugada" : imageOnly ? "Dibujar la tarea en la pizarra" : "Crear tarea con pizarra táctica"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {viewOnly
              ? "Reproduciendo la jugada guardada. Usa los controles de escenas para verla de nuevo o cambiar la velocidad."
              : imageOnly
              ? "Coloca elementos en el campo y pulsa Guardar para usar el dibujo como imagen de la tarea."
              : "Coloca elementos en el campo y guárdalos como imagen de la tarea, o sube directamente una imagen ya hecha. Opcionalmente puedes guardarla también en tu biblioteca."}
          </DialogDescription>
        </DialogHeader>

        {showTools && (<>
        {/* Campo + formación + grupo rápido */}
        <div className="flex items-center gap-3 pt-2 overflow-x-auto flex-nowrap sm:flex-wrap [&>*]:shrink-0 pb-1">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Campo:</span>
            <div className="flex rounded-md border border-slate-200 overflow-hidden">
              {Object.entries(PITCH_TYPES).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setPitchType(key)}
                  className={`px-3 py-1 text-xs font-semibold transition ${
                    pitchType === key ? "bg-purple-600 text-white" : "bg-white text-slate-600 hover:bg-purple-50"
                  }`}
                  data-testid={`board-pitch-${key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Formación:</span>
            <select
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                applyFormation(v, groupColor);
                e.target.value = "";
              }}
              defaultValue=""
              className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white text-slate-700 hover:border-purple-300 focus:outline-none focus:border-purple-500"
              data-testid="board-formation"
            >
              <option value="" disabled>Plantilla rápida...</option>
              {Object.keys(FORMATIONS).map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Ejercicio:</span>
            <select
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return;
                applyTemplate(v);
                e.target.value = "";
              }}
              defaultValue=""
              className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white text-slate-700 hover:border-purple-300 focus:outline-none focus:border-purple-500"
              data-testid="board-template"
            >
              <option value="" disabled>Plantilla de ejercicio...</option>
              {TEMPLATE_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Grupo:</span>
            <select
              value={groupColor}
              onChange={(e) => setGroupColor(e.target.value)}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
              data-testid="board-group-color"
            >
              {Object.entries(PLAYER_COLORS).map(([k, c]) => (
                <option key={k} value={k}>{c.label}</option>
              ))}
            </select>
            <select
              value={groupCount}
              onChange={(e) => setGroupCount(Number(e.target.value))}
              className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white"
              data-testid="board-group-count"
            >
              {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <Button size="sm" variant="outline" onClick={() => addGroup(groupColor, groupCount)}
              className="h-7 px-2 text-xs font-bold text-purple-700 border-purple-300 hover:bg-purple-50"
              data-testid="board-group-create">
              + Crear
            </Button>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">División:</span>
            {[
              { key: "thirds", label: "Tercios", Icon: Columns3 },
              { key: "lanes", label: "Carriles", Icon: Rows3 },
              { key: "grid", label: "Rejilla", Icon: Grid2x2 },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setOverlays((o) => ({ ...o, [key]: !o[key] }))}
                className={`flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-semibold transition ${
                  overlays[key]
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-purple-50"
                }`}
                data-testid={`board-overlay-${key}`}
              >
                <Icon className="h-3 w-3" />{label}
              </button>
            ))}
          </div>
        </div>

        {/* Elementos */}
        <div className="flex items-center gap-1.5 py-2 border-y border-slate-200 overflow-x-auto flex-nowrap sm:flex-wrap [&>*]:shrink-0">
          {roster.length > 0 && !viewOnly && (
            <>
              <Select value={rosterSel} onValueChange={addRosterPlayer}>
                <SelectTrigger
                  className="h-8 w-48 text-xs font-bold border-purple-300 text-purple-700 bg-purple-50/60"
                  data-testid="board-roster-select"
                >
                  <SelectValue placeholder={`Plantilla (${availableRoster.length} disp.)`} />
                </SelectTrigger>
                <SelectContent>
                  {availableRoster.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400" data-testid="board-roster-empty">
                      Todos los jugadores están en la pizarra
                    </div>
                  )}
                  {availableRoster.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`board-roster-${p.id}`}>
                      {p.dorsal != null && p.dorsal !== "" ? `#${p.dorsal} · ` : ""}{p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="w-px h-6 bg-slate-200 mx-1" />
            </>
          )}
          {Object.entries(PLAYER_COLORS).map(([key, c]) => (
            <button
              key={key}
              type="button"
              title={`Jugador ${c.label}`}
              onClick={() => addPlayer(key)}
              className="w-7 h-7 rounded-full border-2 border-white shadow ring-1 ring-slate-300 hover:scale-110 transition text-[10px] font-black flex items-center justify-center"
              style={{ background: c.fill, color: c.text }}
              data-testid={`board-add-${key}`}
            >
              {key === "portero" ? "P" : "+"}
            </button>
          ))}
          <span className="w-px h-6 bg-slate-200 mx-1" />
          {MATERIAL_PRESETS.map(({ type, label, Icon }) => (
            <Button key={type} size="sm" variant="outline" onClick={() => addElement(type)}
              className="h-8 px-2 text-xs font-semibold" data-testid={`board-add-${type}`}>
              <Icon className="h-3.5 w-3.5 mr-1" />
              {label}
            </Button>
          ))}
          <span className="w-px h-6 bg-slate-200 mx-1" />
          {LINE_PRESETS.map(({ type, label, Icon }) => (
            <Button key={type} size="sm" variant="outline" onClick={() => addElement(type)}
              className="h-8 px-2 text-xs font-semibold" data-testid={`board-add-${type}`}>
              <Icon className="h-3.5 w-3.5 mr-1" />
              {label}
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => addElement("zone", { shape: "circle" })}
            className="h-8 px-2 text-xs font-semibold" title="Zona circular" data-testid="board-add-zone-circle">
            <CircleIcon className="h-3.5 w-3.5 mr-1" />
            Zona ⭕
          </Button>
          <Button size="sm" variant={tool === "poly" ? "default" : "outline"}
            onClick={() => { polyRef.current = []; setPolyPts([]); setTool(tool === "poly" ? null : "poly"); }}
            className={`h-8 px-2 text-xs font-semibold ${tool === "poly" ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
            title="Zona libre: haz clic para añadir vértices y pulsa Finalizar" data-testid="board-add-zone-poly">
            <Hexagon className="h-3.5 w-3.5 mr-1" />
            Zona libre
          </Button>
        </div>

        {/* Flechas con significado táctico */}
        <div className="flex items-center gap-1.5 overflow-x-auto flex-nowrap sm:flex-wrap [&>*]:shrink-0">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mr-1">Tácticas</span>
          {TACTIC_PRESETS.map(({ key, label, desc, Icon }) => (
            <Button key={key} size="sm" variant="outline" onClick={() => addTactic(key)}
              className="h-8 px-2 text-xs font-semibold" title={desc} data-testid={`board-tactic-${key}`}>
              <Icon className="h-3.5 w-3.5 mr-1" />
              {label}
            </Button>
          ))}
        </div>
        </>)}

        {/* Acciones */}
        <div className="flex items-center gap-1.5 overflow-x-auto flex-nowrap sm:flex-wrap [&>*]:shrink-0">
          <Button size="sm" variant="ghost" onClick={undo} disabled={!canUndo}
            className="h-8 px-2 text-xs" title="Deshacer (Ctrl+Z)" data-testid="board-undo">
            <Undo2 className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={redo} disabled={!canRedo}
            className="h-8 px-2 text-xs" title="Rehacer (Ctrl+Y)" data-testid="board-redo">
            <Redo2 className="h-3.5 w-3.5" />
          </Button>
          <span className="w-px h-6 bg-slate-200" />
          <Button size="sm" variant="ghost" onClick={() => setZoom((z) => clamp(Math.round((z - 0.25) * 100) / 100, 1, 3))}
            disabled={zoom <= 1} className="h-8 px-2 text-xs" title="Alejar" data-testid="board-zoom-out">
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <span className="text-[11px] font-bold text-slate-600 w-10 text-center">{Math.round(zoom * 100)}%</span>
          <Button size="sm" variant="ghost" onClick={() => setZoom((z) => clamp(Math.round((z + 0.25) * 100) / 100, 1, 3))}
            disabled={zoom >= 3} className="h-8 px-2 text-xs" title="Acercar" data-testid="board-zoom-in">
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setZoom(1)} disabled={zoom === 1}
            className="h-8 px-2 text-xs" title="Ajustar (100%)" data-testid="board-zoom-fit">
            <Maximize className="h-3.5 w-3.5" />
          </Button>
          <span className="w-px h-6 bg-slate-200" />
          <Button size="sm" variant={lockField ? "default" : "ghost"}
            onClick={() => setLockField((v) => !v)}
            className={`h-8 px-2 text-xs ${lockField ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
            title={lockField ? "Campo bloqueado: solo zoom/paneo (toca para desbloquear)" : "Bloquear campo (evita mover elementos por accidente)"}
            data-testid="board-lock-field">
            {lockField ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setToolbarCollapsed((v) => !v)}
            className="h-8 px-2 text-xs" title={toolbarCollapsed ? "Mostrar herramientas" : "Plegar herramientas (más campo)"}
            data-testid="board-toolbar-collapse">
            {toolbarCollapsed ? <ChevronsDown className="h-3.5 w-3.5" /> : <ChevronsUp className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={toggleFullscreen}
            className="h-8 px-2 text-xs" title={fsActive ? "Salir de pantalla completa" : "Pantalla completa (ideal en tablet)"}
            data-testid="board-fullscreen">
            {fsActive ? <Minimize className="h-3.5 w-3.5" /> : <Expand className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant={snapGrid ? "default" : "ghost"} onClick={() => setSnapGrid((v) => !v)}
            className={`h-8 px-2 text-xs ${snapGrid ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}`}
            title="Ajustar a cuadrícula al mover" data-testid="board-snap-grid">
            <Magnet className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant={showGuides ? "default" : "ghost"} onClick={() => setShowGuides((v) => !v)}
            className={`h-8 px-2 text-xs ${showGuides ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}`}
            title="Reglas y guías (líneas de centro y cuartos)" data-testid="board-guides">
            <Ruler className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant={showMeters ? "default" : "ghost"} onClick={() => setShowMeters((v) => !v)}
            className={`h-8 px-2 text-xs ${showMeters ? "bg-sky-600 hover:bg-sky-700 text-white" : ""}`}
            title="Escala en metros / cuadrícula real" data-testid="board-meters">
            <Ruler className="h-3.5 w-3.5 mr-1" /> m
          </Button>
          {showMeters && (
            <select value={meterStep} onChange={(e) => setMeterStep(Number(e.target.value))}
              className="h-8 rounded border border-slate-200 text-xs px-1" data-testid="board-meter-step">
              <option value={5}>5×5 m</option>
              <option value={10}>10×10 m</option>
              <option value={15}>15×15 m</option>
            </select>
          )}
          {showMeters && (
            <span className="text-[11px] font-bold text-sky-700 whitespace-nowrap" data-testid="board-density">
              {_pt.realW}×{_pt.realH} m · {fieldArea} m²
              {densityPerPlayer != null ? ` · ${densityPerPlayer} m²/jug (${playerCount})` : ""}
            </span>
          )}
          <Button size="sm" variant={tool === "measure" ? "default" : "ghost"}
            onClick={() => { measureRef.current = null; setTool(tool === "measure" ? null : "measure"); }}
            className={`h-8 px-2 text-xs ${tool === "measure" ? "bg-sky-600 hover:bg-sky-700 text-white" : ""}`}
            title="Medir distancia: clic en dos puntos" data-testid="board-measure">
            📏
          </Button>
          <select value={fmt} onChange={(e) => adaptFormat(e.target.value)}
            className="h-8 rounded border border-slate-200 text-xs px-1 font-bold" title="Adaptar la tarea a otro formato"
            data-testid="board-format">
            <option value="F11">F11</option>
            <option value="F7">F7</option>
            <option value="F5">F5</option>
          </select>
          <div className="flex-1" />
          {selectedIds.length > 0 && (
            <>
              <span className="text-[11px] font-bold text-purple-700">{selectedIds.length} sel.</span>
              {selectedIds.length >= 2 && (
                <>
                  <Button size="sm" variant="ghost" onClick={() => alignSelected("h")}
                    className="h-8 px-1.5 text-xs" title="Alinear en fila (misma altura)" data-testid="board-align-h">
                    <AlignCenterHorizontal className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => alignSelected("v")}
                    className="h-8 px-1.5 text-xs" title="Alinear en columna (misma vertical)" data-testid="board-align-v">
                    <AlignCenterVertical className="h-3.5 w-3.5" />
                  </Button>
                  {selectedIds.length >= 3 && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => alignSelected("dist-h")}
                        className="h-8 px-1.5 text-xs" title="Distribuir horizontalmente" data-testid="board-dist-h">
                        <AlignHorizontalDistributeCenter className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => alignSelected("dist-v")}
                        className="h-8 px-1.5 text-xs" title="Distribuir verticalmente" data-testid="board-dist-v">
                        <AlignVerticalDistributeCenter className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </>
              )}
              <Button size="sm" variant="ghost" onClick={() => duplicateSelected(1)}
                className="h-8 px-2 text-xs text-purple-700 hover:bg-purple-50" data-testid="board-duplicate-selected">
                <Copy className="h-3.5 w-3.5 mr-1" /> Duplicar
              </Button>
              {selectedIds.length >= 2 && (
                <>
                  <Button size="sm" variant="ghost" onClick={groupSelected}
                    className="h-8 px-1.5 text-xs" title="Agrupar elementos" data-testid="board-group">
                    <Group className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={ungroupSelected}
                    className="h-8 px-1.5 text-xs" title="Desagrupar" data-testid="board-ungroup">
                    <Ungroup className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
              <Button size="sm" variant="ghost" onClick={toggleLockSelected}
                className="h-8 px-1.5 text-xs" title="Bloquear / desbloquear elemento" data-testid="board-lock-el">
                {elements.some((e) => selSet.has(e.id) && e.locked) ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={bringToFront}
                className="h-8 px-1.5 text-xs" title="Traer al frente" data-testid="board-front">
                <BringToFront className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={sendToBack}
                className="h-8 px-1.5 text-xs" title="Enviar atrás" data-testid="board-back">
                <SendToBack className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => flipSelected("h")}
                className="h-8 px-1.5 text-xs" title="Reflejar horizontalmente" data-testid="board-flip-h">
                <FlipHorizontal className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => flipSelected("v")}
                className="h-8 px-1.5 text-xs" title="Reflejar verticalmente" data-testid="board-flip-v">
                <FlipVertical className="h-3.5 w-3.5" />
              </Button>
              <select
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (n) duplicateSelected(n);
                  e.target.value = "";
                }}
                defaultValue=""
                className="text-xs border border-purple-200 text-purple-700 rounded-md px-1.5 py-1 bg-white h-8"
                title="Duplicar × N"
                data-testid="board-duplicate-times"
              >
                <option value="" disabled>×N</option>
                {DUP_OPTIONS.map((n) => (
                  <option key={n} value={n}>×{n}</option>
                ))}
              </select>
              <Button size="sm" variant="ghost" onClick={removeSelected}
                className="h-8 px-2 text-xs text-red-600 hover:bg-red-50" data-testid="board-delete-selected">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Borrar
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={clearBoard} disabled={elements.length === 0}
            className="h-8 px-2 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
            title="Vaciar pizarra" data-testid="board-clear">
            <Eraser className="h-3.5 w-3.5 mr-1" /> Vaciar
          </Button>
          <span className="text-[11px] text-slate-500 hidden lg:inline">
            <MousePointer2 className="inline h-3 w-3 mr-0.5" /> arrastra en el campo para seleccionar varios · Shift+clic añade
          </span>
        </div>

        {/* Escenas / Animación */}
        <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 overflow-x-auto flex-nowrap sm:flex-wrap [&>*]:shrink-0">
          <span className="text-[11px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">
            <Film className="h-3.5 w-3.5" /> Escenas:
          </span>
          {scenes.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => switchScene(i)}
              disabled={playing}
              className={`w-7 h-7 rounded-md text-xs font-black transition border ${
                i === sceneIdx
                  ? "bg-purple-600 text-white border-purple-600"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-purple-50"
              }`}
              data-testid={`board-scene-${i}`}
            >
              {i + 1}
            </button>
          ))}
          {scenes.length > 1 && (
            <>
              <span className="w-px h-5 bg-slate-200 mx-0.5" />
              <Button size="sm" variant="ghost" onClick={() => switchScene(sceneIdx - 1)}
                disabled={playing || sceneIdx === 0} className="h-7 w-7 p-0"
                title="Fase anterior (avance manual)" data-testid="board-scene-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => switchScene(sceneIdx + 1)}
                disabled={playing || sceneIdx === scenes.length - 1} className="h-7 w-7 p-0"
                title="Fase siguiente (avance manual)" data-testid="board-scene-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => moveScene(-1)}
                disabled={playing || sceneIdx === 0} className="h-7 w-7 p-0 text-slate-500"
                title="Mover esta escena a la izquierda (reordenar)" data-testid="board-scene-move-left">
                <MoveLeft className="h-3.5 w-3.5" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => moveScene(1)}
                disabled={playing || sceneIdx === scenes.length - 1} className="h-7 w-7 p-0 text-slate-500"
                title="Mover esta escena a la derecha (reordenar)" data-testid="board-scene-move-right">
                <MoveRight className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={addScene} disabled={playing || scenes.length >= 8}
            className="h-7 px-2 text-xs font-bold text-purple-700 border-purple-300 hover:bg-purple-50"
            title="Duplica la escena actual como siguiente fase de la jugada"
            data-testid="board-scene-add">
            + Escena
          </Button>
          {scenes.length > 1 && (
            <Button size="sm" variant="ghost" onClick={deleteScene} disabled={playing}
              className="h-7 px-1.5 text-xs text-red-600 hover:bg-red-50"
              title="Eliminar la escena actual" data-testid="board-scene-delete">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {!playing && (
            <Button size="sm" variant="outline" onClick={exportPhasesPdf} disabled={exportingPdf}
              className="h-7 px-2.5 text-xs font-bold border-purple-300 text-purple-700 hover:bg-purple-50"
              title="Exporta un PDF con una fase por página y la leyenda táctica"
              data-testid="board-export-phases-pdf">
              <FileText className="h-3.5 w-3.5 mr-1" /> {exportingPdf ? "Generando PDF…" : "PDF por fases"}
            </Button>
          )}
          {scenes.length > 1 && (
            <>
              <span className="w-px h-5 bg-slate-200 mx-1" />
              {playing ? (
                <Button size="sm" onClick={stopAnimation}
                  className="h-7 px-2.5 text-xs font-bold bg-red-600 hover:bg-red-700 text-white"
                  data-testid="board-anim-stop">
                  <StopCircle className="h-3.5 w-3.5 mr-1" /> Parar
                </Button>
              ) : (
                <Button size="sm" onClick={playAnimation}
                  className="h-7 px-2.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="board-anim-play">
                  <Play className="h-3.5 w-3.5 mr-1" /> Reproducir
                </Button>
              )}
              <select
                value={animSpeed}
                onChange={(e) => setAnimSpeed(Number(e.target.value))}
                disabled={playing}
                className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white h-7"
                data-testid="board-anim-speed"
              >
                <option value={2000}>Lenta</option>
                <option value={1200}>Normal</option>
                <option value={700}>Rápida</option>
              </select>
              {!playing && (
                <Button size="sm" variant="outline" onClick={exportGif} disabled={exportingGif}
                  className="h-7 px-2.5 text-xs font-bold border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  title="Genera un GIF animado de la jugada para compartir por WhatsApp"
                  data-testid="board-share-gif">
                  <Share2 className="h-3.5 w-3.5 mr-1" /> {exportingGif ? "Generando GIF..." : "Compartir GIF"}
                </Button>
              )}
            </>
          )}
          <span className="text-[11px] text-slate-400 hidden xl:inline ml-1">
            Cada escena es una fase de la jugada: muévelo todo y pulsa Reproducir
          </span>
        </div>

        {tool === "poly" && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="board-poly-bar">
            <span className="text-xs font-bold text-amber-800">Zona libre: haz clic en el campo para añadir vértices ({polyPts.length})</span>
            <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700 text-white" onClick={finishPoly}
              disabled={polyPts.length < 3} data-testid="board-poly-finish">Finalizar</Button>
            <Button size="sm" variant="ghost" className="h-7" onClick={cancelDraw} data-testid="board-poly-cancel">Cancelar</Button>
          </div>
        )}
        {tool === "measure" && (
          <div className="flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-md px-3 py-2" data-testid="board-measure-bar">
            <span className="text-xs font-bold text-sky-800">Medir distancia: haz clic en dos puntos del campo</span>
            <Button size="sm" variant="ghost" className="h-7" onClick={cancelDraw} data-testid="board-measure-cancel">Cancelar</Button>
          </div>
        )}


        {/* Propiedades del elemento seleccionado */}
        {singleSel && isPlayerEl(singleSel) && (
          <div className="flex flex-wrap items-center gap-2 bg-purple-50 border border-purple-200 rounded-md px-2 py-1.5" data-testid="board-player-props">
            <span className="text-[10px] uppercase tracking-widest font-bold text-purple-700">Jugador:</span>
            <Input
              value={singleSel.label || ""}
              onChange={(e) => patchSelected({ label: e.target.value.slice(0, 3) })}
              className="w-14 h-7 text-xs text-center"
              placeholder="Nº"
              data-testid="board-player-dorsal"
            />
            <Input
              value={singleSel.name || ""}
              onChange={(e) => patchSelected({ name: e.target.value.slice(0, 14) })}
              className="w-32 h-7 text-xs"
              placeholder="Nombre"
              data-testid="board-player-name"
            />
            <div className="flex items-center gap-1">
              {Object.entries(PLAYER_COLORS).map(([k, c]) => (
                <button
                  key={k}
                  type="button"
                  title={c.label}
                  onClick={() => patchSelected({ type: "player", color: k })}
                  className={`w-5 h-5 rounded-full border ${playerColorKey(singleSel) === k ? "ring-2 ring-purple-600" : "ring-1 ring-slate-300"}`}
                  style={{ background: c.fill }}
                  data-testid={`board-player-color-${k}`}
                />
              ))}
            </div>
            <span className="text-[10px] text-slate-500 font-bold ml-1">Tamaño</span>
            <input
              type="range" min="0.6" max="2" step="0.1"
              value={singleSel.scale ?? 1}
              onChange={(e) => patchSelected({ scale: Number(e.target.value) })}
              className="w-24 accent-purple-600"
              data-testid="board-player-size"
            />
          </div>
        )}
        {singleSel && (String(singleSel.type).startsWith("arrow_") || singleSel.type === "line") && (
          <div className="flex flex-wrap items-center gap-2 bg-purple-50 border border-purple-200 rounded-md px-2 py-1.5" data-testid="board-arrow-props">
            <span className="text-[10px] uppercase tracking-widest font-bold text-purple-700">
              {singleSel.type === "line" ? "Línea:" : "Flecha:"}
            </span>
            <div className="flex items-center gap-1">
              {ARROW_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => patchSelected({ stroke: c })}
                  className={`w-5 h-5 rounded-full border ${(singleSel.stroke || "") === c ? "ring-2 ring-purple-600" : "ring-1 ring-slate-300"}`}
                  style={{ background: c }}
                  data-testid={`board-arrow-color-${c.replace("#", "")}`}
                />
              ))}
            </div>
            <span className="text-[10px] text-slate-500 font-bold">Grosor</span>
            <input
              type="range" min="2" max="7" step="1"
              value={singleSel.width || 3}
              onChange={(e) => patchSelected({ width: Number(e.target.value) })}
              className="w-20 accent-purple-600"
              data-testid="board-arrow-width"
            />
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <input
                type="checkbox"
                checked={singleSel.dashed ?? singleSel.type === "arrow_pass"}
                onChange={(e) => patchSelected({ dashed: e.target.checked })}
                data-testid="board-arrow-dashed"
              />
              Discontinua
            </label>
          </div>
        )}
        {singleSel && singleSel.type === "zone" && (
          <div className="flex flex-wrap items-center gap-2 bg-purple-50 border border-purple-200 rounded-md px-2 py-1.5" data-testid="board-zone-props">
            <span className="text-[10px] uppercase tracking-widest font-bold text-purple-700">Zona:</span>
            <div className="flex items-center gap-1">
              {Object.entries(ZONE_COLORS).map(([k, zc]) => (
                <button
                  key={k}
                  type="button"
                  title={k}
                  onClick={() => patchSelected({ zcolor: k })}
                  className={`w-5 h-5 rounded border ${(singleSel.zcolor || "amarillo") === k ? "ring-2 ring-purple-600" : "ring-1 ring-slate-300"}`}
                  style={{ background: zc.fill }}
                  data-testid={`board-zone-color-${k}`}
                />
              ))}
            </div>
            <Input
              value={singleSel.label || ""}
              onChange={(e) => patchSelected({ label: e.target.value.slice(0, 24) })}
              className="w-36 h-7 text-xs"
              placeholder="Etiqueta (ej. Zona presión)"
              data-testid="board-zone-label"
            />
            <span className="text-[10px] text-slate-500 font-bold">Opacidad</span>
            <input
              type="range" min="0.1" max="0.6" step="0.05"
              value={singleSel.opacity ?? 0.25}
              onChange={(e) => patchSelected({ opacity: Number(e.target.value) })}
              className="w-20 accent-purple-600"
              data-testid="board-zone-opacity"
            />
          </div>
        )}
        {singleSel && singleSel.type === "text" && (
          <div className="flex flex-wrap items-center gap-2 bg-purple-50 border border-purple-200 rounded-md px-2 py-1.5" data-testid="board-text-props">
            <span className="text-[10px] uppercase tracking-widest font-bold text-purple-700">Texto:</span>
            <Input
              value={singleSel.label || ""}
              onChange={(e) => patchSelected({ label: e.target.value })}
              className="w-48 h-7 text-xs"
              data-testid="board-text-value"
            />
            <span className="text-[10px] text-slate-500 font-bold">Tamaño</span>
            <input
              type="range" min="9" max="28" step="1"
              value={singleSel.fontSize || 13}
              onChange={(e) => patchSelected({ fontSize: Number(e.target.value) })}
              className="w-24 accent-purple-600"
              data-testid="board-text-size"
            />
          </div>
        )}

        {/* Pitch */}
        <div className="bg-slate-100 rounded-md p-2 select-none">
          <svg
            ref={svgRef}
            viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
            className="w-full h-auto rounded touch-none"
            style={{ aspectRatio: `${baseVB[2]} / ${baseVB[3]}`, cursor: tool ? "crosshair" : undefined }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onPointerDown={onSvgPointerDown}
            onWheel={onWheel}
            data-testid="tactical-board-svg"
          >
            <Pitch />
            <FieldOverlays vb={baseVB} thirds={overlays.thirds} lanes={overlays.lanes} grid={overlays.grid || snapGrid} />
            {showGuides && (
              <g pointerEvents="none">
                <line x1={baseVB[2] / 2} y1="0" x2={baseVB[2] / 2} y2={baseVB[3]} stroke="#A855F7" strokeWidth="1" strokeDasharray="6 5" opacity="0.6" />
                <line x1="0" y1={baseVB[3] / 2} x2={baseVB[2]} y2={baseVB[3] / 2} stroke="#A855F7" strokeWidth="1" strokeDasharray="6 5" opacity="0.6" />
                <line x1={baseVB[2] / 4} y1="0" x2={baseVB[2] / 4} y2={baseVB[3]} stroke="#A855F7" strokeWidth="0.6" strokeDasharray="3 6" opacity="0.4" />
                <line x1={(baseVB[2] * 3) / 4} y1="0" x2={(baseVB[2] * 3) / 4} y2={baseVB[3]} stroke="#A855F7" strokeWidth="0.6" strokeDasharray="3 6" opacity="0.4" />
              </g>
            )}
            {showMeters && (
              <g pointerEvents="none">
                {meterLines.v.map((l, i) => (
                  <g key={`mv${i}`}>
                    <line x1={l.x} y1={_inset} x2={l.x} y2={baseVB[3] - _inset} stroke="#0EA5E9" strokeWidth="0.5" strokeDasharray="2 4" opacity="0.5" />
                    <text x={l.x + 1} y={_inset + 8} fontSize="7" fill="#0369A1" fontWeight="700">{l.m}m</text>
                  </g>
                ))}
                {meterLines.h.map((l, i) => (
                  <line key={`mh${i}`} x1={_inset} y1={l.y} x2={baseVB[2] - _inset} y2={l.y} stroke="#0EA5E9" strokeWidth="0.5" strokeDasharray="2 4" opacity="0.5" />
                ))}
              </g>
            )}
            {elements.map((el) => (
              <g key={el.id}>
                <ElementShape
                  el={el}
                  selected={selSet.has(el.id)}
                  onPointerDown={onPointerDown}
                />
                {el.type === "zone" && (
                  <text x={el.x + 4} y={el.y + (el.shape === "circle" ? (el.h || 70) / 2 : 12)} fontSize="9" fontWeight="900"
                    fill="#0F172A" stroke="#fff" strokeWidth="2" paintOrder="stroke" pointerEvents="none">
                    Z{zoneIds.indexOf(el.id) + 1}
                  </text>
                )}
                {el.type === "measure" && (() => {
                  const dist = Math.hypot(Math.abs(el.x2 - el.x) / uPerMx, Math.abs(el.y2 - el.y) / uPerMy).toFixed(1);
                  const txt = `${dist} m`;
                  const mx = (el.x + el.x2) / 2;
                  const my = (el.y + el.y2) / 2;
                  const bw = txt.length * 6.2 + 8;
                  return (
                    <g pointerEvents="none">
                      <rect x={mx - bw / 2} y={my - 16} width={bw} height={15} rx={3}
                        fill="#0EA5E9" stroke="#fff" strokeWidth="1" />
                      <text x={mx} y={my - 5} textAnchor="middle" fontSize="10" fontWeight="900" fill="#ffffff">
                        {txt}
                      </text>
                    </g>
                  );
                })()}
                {el.locked && (
                  <circle cx={el.x} cy={el.y} r={4} fill="#F59E0B" stroke="#fff" strokeWidth="1" pointerEvents="none" />
                )}
              </g>
            ))}
            {tool === "poly" && polyPts.length > 0 && (
              <g pointerEvents="none">
                <polyline points={polyPts.map((p) => p.join(",")).join(" ")} fill="none" stroke="#F59E0B" strokeWidth="2" strokeDasharray="4 3" />
                {polyPts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="3.5" fill="#F59E0B" stroke="#fff" strokeWidth="1" />)}
              </g>
            )}
            {marquee && (
              <rect
                x={Math.min(marquee.x0, marquee.x1)}
                y={Math.min(marquee.y0, marquee.y1)}
                width={Math.abs(marquee.x1 - marquee.x0)}
                height={Math.abs(marquee.y1 - marquee.y0)}
                fill="#A855F7" fillOpacity="0.12"
                stroke="#7E22CE" strokeWidth="1.5" strokeDasharray="4 3"
                pointerEvents="none"
              />
            )}
          </svg>
        </div>

        {/* Leyenda automática — solo tácticas presentes en la pizarra */}
        {(() => {
          const used = [];
          const seen = new Set();
          elements.forEach((el) => {
            if (el.tactic && TACTIC_BY_KEY[el.tactic] && !seen.has(el.tactic)) {
              seen.add(el.tactic);
              used.push(TACTIC_BY_KEY[el.tactic]);
            }
          });
          if (used.length === 0) return null;
          const glyph = (t) => {
            if (t.base === "arrow_drib") return "∿";
            if (t.base === "arrow_curve") return "⌒";
            if (t.base === "arrow_rotation") return "↻";
            return t.dashed ? "⇢" : (t.width >= 6 ? "➜" : "→");
          };
          return (
            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid="board-legend">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Leyenda</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {used.map((t) => (
                  <span key={t.key} className="inline-flex items-center gap-1.5 text-xs text-slate-700" data-testid={`legend-${t.key}`}>
                    <span className="font-black text-base leading-none" style={{ color: t.stroke === "#FFFFFF" ? "#475569" : t.stroke }}>{glyph(t)}</span>
                    {t.desc}
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Metadata */}
        {!imageOnly && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Título (para la biblioteca)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej. Rondo 4v2 con transición" data-testid="board-title" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Objetivo</Label>
            <Input value={objective} onChange={(e) => setObjective(e.target.value)}
              placeholder="Ej. Mantener la posesión" data-testid="board-objective" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500">Etiquetas (separadas por coma)</Label>
            <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)}
              placeholder="rondo, posesión" data-testid="board-tags" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500">Descripción</Label>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Explicación de la tarea..." data-testid="board-description" />
          </div>
        </div>
        )}

        {!imageOnly && (
        <>
        <label className="flex items-center gap-2 text-sm text-slate-700 pt-1">
          <input
            type="checkbox"
            checked={saveToLibrary}
            onChange={(e) => setSaveToLibrary(e.target.checked)}
            data-testid="board-save-to-library"
          />
          <span>
            Guardar también en mi <b>biblioteca de tareas</b>
            <span className="text-xs text-slate-400 ml-1">(requiere título)</span>
          </span>
        </label>
        {saveToLibrary && (
          <label className="flex items-center gap-2 text-sm text-slate-700 -mt-1 ml-6">
            <input
              type="checkbox"
              checked={shareWithClub}
              onChange={(e) => setShareWithClub(e.target.checked)}
              data-testid="board-share-with-club"
            />
            <span>
              Compartir con todo el club
              <span className="text-xs text-slate-400 ml-1">(visible para cualquier entrenador)</span>
            </span>
          </label>
        )}
        </>
        )}

        <DialogFooter className="flex flex-row justify-end gap-2 pt-2 flex-wrap">
          {!viewOnly && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                onChange={onPickImageFile}
                className="hidden"
                data-testid="board-upload-input"
              />
              <Button
                variant="outline"
                onClick={handleUploadImage}
                disabled={saving}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                title="Sube una imagen directamente sin dibujar en la pizarra"
                data-testid="board-upload-image"
              >
                <Upload className="h-4 w-4 mr-1.5" /> Subir imagen
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="board-cancel">
            <X className="h-4 w-4 mr-1.5" /> {viewOnly ? "Cerrar" : "Cancelar"}
          </Button>
          {!viewOnly && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
              data-testid="board-save"
            >
              <Save className="h-4 w-4 mr-1.5" /> {saving ? "Guardando..." : "Guardar"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function clamp(v, min, max) {
  return Math.max(min, Math.min(max, v));
}
